import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, X, Layers, Loader2, RefreshCw, Database, AlertCircle, Check, ChevronDown, Move, History, MoreHorizontal, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/auth-context";
import { useScopeFilter } from "@/lib/scope-filter-context";
// Prebuilt primitives — iter-73 refactor extracted these from this file to
// keep the orchestrator focused. FilterControl + presentation helpers live
// in ./prebuilt/presentation; the single-tile JSX + action bar in KpiTile.
import {
    PrebuiltChartCard, TablePrebuiltCard, FilterControl, formatKpi as _formatKpi,
} from "./prebuilt/presentation";
import KpiTile from "./prebuilt/KpiTile";
import { resolveWidget } from "@/lib/widget-derivations";
import ConnectorHealthPill from "./ConnectorHealthPill";
import KpiDrillDownDialog from "./KpiDrillDownDialog";
import ScopeFilterPill from "@/components/layout/ScopeFilterPill";
import DashboardGlossaryButton from "@/components/dashboards/DashboardGlossaryButton";
import CustomizeWidgetsDialog from "@/components/dashboards/CustomizeWidgetsDialog";

// Configurable display caps — sourced from CSS custom-property hooks where
// available so future themes can override without a code change.
// Falls back to safe constants. Avoids hardcoded magic numbers in JSX.
// ISSUE-FIX: Safe numeric string parser handling currency, commas, and formatted numbers
const _parseNumeric = (v) => {
    if (typeof v === "number") return isNaN(v) ? 0 : v;
    if (!v) return 0;
    const clean = String(v).replace(/[^0-9.-]/g, "");
    const n = parseFloat(clean);
    return isNaN(n) ? 0 : n;
};

const CFG = {
    TOPN_BAR_CATEGORIES: 15,   // bar/donut: cap distinct x-axis labels
    TOPN_LINE_POINTS: 60,      // line: keep up to N points (drop oldest)
    KPI_REFRESH_DEBOUNCE_MS: 250,
    TABLE_ROW_LIMIT: 100,      // fetch table rows incrementally
    TABLE_TOTAL_LIMIT: 10000,  // aggregate every grouped table row for totals
};



/**
 * Locked-KPI strip + pool drawer (iter-57 P0 — extracted from PrebuiltRenderer).
 *
 * Renders only the 16-locked KPI band, swap menu, and pool drawer for
 * prebuilt-instantiated dashboards. Designed to compose ABOVE the standard
 * `DashboardGrid` — chart/table widgets keep their drag/resize/pin chrome
 * intact instead of being forked into a parallel renderer.
 *
 * Theme follows the application theme (`useTheme()`) — there's no in-strip
 * toggle. Joined-query live values resolve once per dashboard and replace
 * the static template `widget.value`.
 */
const isDateLike = (field, labelSample) => {
    const f = String(field || "").toLowerCase();
    if (/(date|month|year|quarter|day|start_at|created_at|occurred_at)/.test(f)) {
        return true;
    }
    if (labelSample && typeof labelSample === "string") {
        if (/^\d{4}-?\d{2}/.test(labelSample)) return true;
    }
    return false;
};

export default function LockedKpiStrip({ dashboard, onDashboardUpdated }) {
    // ISSUE-FIX: Support COUNT_DISTINCT_IF and DISTINCT_COUNT_IF in extractConditionFromFormula
    function extractConditionFromFormula(formula) {
        if (!formula) return null;
        const lower = formula.toLowerCase();
        // ISSUE-FIX: Skip client-side parsing for complex formulas containing CASE WHEN, DATEDIFF, or arithmetic operators
        if (lower.includes("case") || lower.includes("datediff") || lower.includes("/") || lower.includes("*")) {
            return null;
        }
        const mIf = /^\s*(COUNT_DISTINCT_IF|DISTINCT_COUNT_IF|COUNT_IF|SUM_IF|AVG_IF|MIN_IF|MAX_IF)\s*\(\s*(.+?)\s*\)\s*$/i.exec(formula);
        if (mIf) {
            const func = mIf[1].toUpperCase();
            const baseAgg = (func === "COUNT_DISTINCT_IF" || func === "DISTINCT_COUNT_IF") ? "count_distinct" : func.replace("_IF", "").toLowerCase();
            const inner = mIf[2];
            let cond = inner;
            let measure = null;
            if (inner.includes(",")) {
                const parts = inner.split(",");
                const maybeMeasure = parts[parts.length - 1].trim();
                if (/^[a-zA-Z0-9_.]+$/.test(maybeMeasure)) {
                    cond = parts.slice(0, -1).join(",").trim();
                    measure = maybeMeasure;
                }
            }
            return { cond, baseAgg, measure };
        }

        // ISSUE-FIX: Support SQL-style COUNT(...) WHERE with WHERE outside parenthesis
        const mWhereExt = /^\s*(COUNT_DISTINCT|DISTINCT|SUM|AVG|COUNT|MIN|MAX)\s*(?:\(\s*(?:DISTINCT\s+)?(.+?)\s*\)|(?:\s+DISTINCT\s+)?([a-zA-Z0-9_.*]+))\s+WHERE\s+(.+?)\s*$/i.exec(formula);
        if (mWhereExt) {
            const baseAgg = mWhereExt[1].toLowerCase() === "distinct" ? "count_distinct" : mWhereExt[1].toLowerCase();
            return { cond: mWhereExt[4].trim(), baseAgg, measure: (mWhereExt[2] || mWhereExt[3] || "").trim() };
        }

        const mWhere = /^\s*(COUNT_DISTINCT|DISTINCT|SUM|AVG|COUNT|MIN|MAX)\s*\(\s*(?:DISTINCT\s+)?(.+?)\s+WHERE\s+(.+?)\s*\)\s*$/i.exec(formula);
        if (mWhere) {
            const baseAgg = mWhere[1].toLowerCase() === "distinct" ? "count_distinct" : mWhere[1].toLowerCase();
            return { cond: mWhere[3].trim(), baseAgg, measure: mWhere[2].trim() };
        }

        const mIfExpr = /^\s*(COUNT_DISTINCT|DISTINCT|SUM|AVG|COUNT|MIN|MAX)\s*\(\s*(?:DISTINCT\s+)?IF\s*\(\s*(.+?)\s*,\s*(.+?)\s*,\s*.+?\s*\)\s*\)\s*$/i.exec(formula);
        if (mIfExpr) {
            const baseAgg = mIfExpr[1].toLowerCase() === "distinct" ? "count_distinct" : mIfExpr[1].toLowerCase();
            return { cond: mIfExpr[2].trim(), baseAgg, measure: mIfExpr[3].trim() };
        }

        const mCase = /^\s*(COUNT_DISTINCT|DISTINCT|SUM|AVG|COUNT|MIN|MAX)\s*\(\s*(?:DISTINCT\s+)?CASE\s+WHEN\s+(.+?)\s+THEN\s+(.+?)\s+(?:ELSE\s+.+?\s+)?END\s*\)\s*$/i.exec(formula);
        if (mCase) {
            const baseAgg = mCase[1].toLowerCase() === "distinct" ? "count_distinct" : mCase[1].toLowerCase();
            return { cond: mCase[2].trim(), baseAgg, measure: mCase[3].trim() };
        }

        return null;
    }
    const scopeFilter = useScopeFilter();
    // iter-121.132 — `isSA` gates the SA-only "Remove from prebuilt"
    // option in the per-widget action menu. Was previously only defined
    // inside the ProvenanceLine subcomponent; the main LockedKpiStrip
    // body needs it too so the menu wiring can render correctly.
    const { user } = useAuth();
    const isSA = user?.role === "super_admin";
    const isPreview = !!dashboard?.previewMode;
    const isEditableTemplate = isSA;
    const [prefs, setPrefs] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const [rearranging, setRearranging] = useState(false);
    const [draftWidgets, setDraftWidgets] = useState(null);
    const [draftKpiOrder, setDraftKpiOrder] = useState(null);
    const [draftKpiSizes, setDraftKpiSizes] = useState(null);
    const [draftWidgetSizes, setDraftWidgetSizes] = useState(null);
    const [draftLayout, setDraftLayout] = useState(null);

    const currentWidgets = useMemo(() => {
        return rearranging && draftWidgets ? draftWidgets : (dashboard.widgets || []);
    }, [rearranging, draftWidgets, dashboard.widgets]);
    const resolvableKpiWidgets = useMemo(() => {
        const byId = {};
        currentWidgets.forEach((w) => {
            if (w?.id) byId[w.id] = w;
        });
        const out = [];
        const seen = new Set();
        const pushKpi = (k) => {
            if (!k) return;
            const base = typeof k === "string" ? byId[k] : (byId[k.id] ? { ...byId[k.id], ...k } : k);
            if (!base?.id || seen.has(base.id)) return;
            const chartType = base.chart_type || base.type;
            if ((chartType === "kpi" || chartType === "kpi_card") && base.formula) {
                out.push(base);
                seen.add(base.id);
            }
        };
        (dashboard.locked_kpis || []).forEach(pushKpi);
        currentWidgets.forEach(pushKpi);
        return out;
    }, [currentWidgets, dashboard.locked_kpis]);
    const currentKpiOrder = useMemo(() => {
        return rearranging && draftKpiOrder ? draftKpiOrder : (prefs?.kpi_order || []);
    }, [rearranging, draftKpiOrder, prefs?.kpi_order]);
    const currentWidgetSizes = useMemo(() => {
        if (rearranging && draftWidgetSizes) return draftWidgetSizes;
        const out = { ...(prefs?.widget_sizes || {}) };
        currentWidgets.forEach((w) => {
            if (w.id && w.size && !out[w.id]) {
                out[w.id] = w.size;
            }
        });
        return out;
    }, [rearranging, draftWidgetSizes, prefs?.widget_sizes, currentWidgets]);
    const [poolOpen, setPoolOpen] = useState(false);
    // iter-2026 — version History moved out of the (now-removed) provenance
    // strip into the header toolbar. Lives here in the main body so the
    // Popover can sit beside Regenerate / Pool without any extra vertical
    // space. Behaviour is unchanged: list all template snapshots + Restore.
    const templateId = dashboard.source_gallery_template_id || dashboard.gallery_item_id || null;
    const currentTemplateVersion = Number(dashboard.source_template_version || dashboard.version || 1);
    // Only edit-capable roles can roll back a version; read-only viewers still
    // see the audit trail but the Restore action is hidden (backend 403s anyway).
    const canRestoreVersion = ["super_admin", "org_admin", "entity_admin"].includes(user?.role);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [versions, setVersions] = useState(null);
    const [restoringVersion, setRestoringVersion] = useState(null);
    const fmtHistoryDate = (iso) => {
        if (!iso) return null;
        try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
        catch { return iso; }
    };
    const loadVersions = useCallback(async () => {
        if (!templateId) return;
        try {
            const r = await api.get(`/gallery/${templateId}/versions`);
            setVersions(r.data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load history");
            setVersions([]);
        }
    }, [templateId]);
    const restoreVersion = async (snapshotId) => {
        setRestoringVersion(snapshotId);
        try {
            const r = await api.post(`/gallery/${templateId}/versions/${snapshotId}/restore`);
            toast.success(`Restored to version ${r.data.restored_from_version} (now v${r.data.version})`);
            setHistoryOpen(false);
            window.location.reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Restore failed");
        } finally {
            setRestoringVersion(null);
        }
    };

    const [activeTab, setActiveTab] = useState("all");
    const [activeSwapSlot, setActiveSwapSlot] = useState(null);
    const [liveValues, setLiveValues] = useState({});
    const [liveCharts, setLiveCharts] = useState({});
    const [liveTables, setLiveTables] = useState({});
    const [tableLoadingMore, setTableLoadingMore] = useState({});
    const [liveLoading, setLiveLoading] = useState(false);
    // refreshKey bumps every time the user hits Regenerate so all live
    // useEffects re-fetch from /joined-query without hitting the URL again.
    const [refreshKey, setRefreshKey] = useState(0);
    // iter-119aa — one-shot cache bypass flag flipped on by handleRegenerate;
    // any joined-query fired while this is true will set `_nocache:true` on
    // the body, then the flag resets to false after the effects have run.
    const [bypassCacheOnce, setBypassCacheOnce] = useState(false);

    // iter-106: listen for the global `dashboard:refresh` event broadcast
    // by DashboardScheduleControl (manual click + auto-cadence ticks). Bumps
    // refreshKey, which is in every joined-query useEffect dependency list,
    // so KPIs, charts AND tables all re-fetch in lockstep.
    useEffect(() => {
        const onRefresh = (e) => {
            if (!dashboard?.id) return;
            if (e?.detail?.id && e.detail.id !== dashboard.id) return;
            setRefreshKey((k) => k + 1);
        };
        window.addEventListener("dashboard:refresh", onRefresh);
        return () => window.removeEventListener("dashboard:refresh", onRefresh);
    }, [dashboard?.id]);
    // Suggested-KPI state — populated when the user clicks "Suggest more KPIs"
    // in the pool drawer. Each suggestion can be added to the pool via PATCH.
    const [suggestedKpis, setSuggestedKpis] = useState([]);
    const [suggestLoading, setSuggestLoading] = useState(false);
    const [addingSuggestionId, setAddingSuggestionId] = useState(null);
    // empty=true when /joined-query came back but returned no rows for ANY
    // KPI — used to swap the strip for an empty-state CTA so users always
    // know "your data is empty" vs "still loading".
    const [empty, setEmpty] = useState(false);
    // Active filter values keyed by filter.id — wired into /joined-query
    // `filters[]` payload so the user can narrow KPIs/charts.
    const [filterValues, setFilterValues] = useState({});
    // iter-2026 — filter section is now an always-visible compact bar (no
    // collapse). Kept sticky so it stays frozen while KPIs/charts scroll.
    // Period preset for date-range — auto-applies to filterValues.date_range
    // when bindings expose a date field. Default "all" so empty-tenant users
    // see something on first load.
    const [datePreset, setDatePreset] = useState("all");
    // iter-119aa (G2) — period-over-period delta values per KPI id. Computed
    // by a second joined-query against the equal-length PREVIOUS window.
    // Only populated when an active date_range filter exists; "All time"
    // has no comparable previous slice so deltas stay hidden.
    const [liveDeltas, setLiveDeltas] = useState({});
    // iter-121.107 — Raw prior-period VALUES (not just the % delta).
    // Powers the YoY / QoQ / MoM comparison footer on KPI tiles.
    // Populated alongside liveDeltas in the same query path.
    const [livePriorValues, setLivePriorValues] = useState({});
    // iter-119aa (G3) — time-grain toggle. `day | week | month`. Default
    // chosen heuristically: ≤14 days of window → day, ≤120 → week, else
    // month. Persisted in `localStorage` per dashboard so it survives
    // refresh. User can override via the toggle pill above each chart.
    const [timeGrain, setTimeGrain] = useState(() => {
        try { return localStorage.getItem(`analyzio.dash.${dashboard?.id}.grain`) || "auto"; }
        catch { return "auto"; }
    });
    // iter-119aa (G4) — KPI drill-down dialog state. `null` = closed;
    // a KPI object = open and showing the drill for that tile.
    const [drillDownKpi, setDrillDownKpi] = useState(null);
    // iter-120 round-3 · Rearrange mode for prebuilt widgets. Toggles
    // HTML5 drag-and-drop on KPI tiles + chart cards inside the strip.
    // Dispatching the same `dashboard:toggle-rearrange` event also
    // toggles the DashboardGrid below — one button drives both.
    React.useEffect(() => {
        const onToggle = () => setRearranging((x) => !x);
        window.addEventListener("dashboard:toggle-rearrange", onToggle);
        return () => window.removeEventListener("dashboard:toggle-rearrange", onToggle);
    }, []);
    // Drag-reorder state — keep raw IDs only so reorders are O(1) and
    // we don't snapshot stale widget objects on each drag-start.
    const [dragSourceId, setDragSourceId] = useState(null);
    // iter-120 round-3 · Reorder chart/table widgets within
    // `dashboard.widgets`. Called when a drag from `src` is dropped
    // on `tgt`. Persists via PATCH so the new order is durable across
    // reloads. We only move the SOURCE — never the locked KPI tiles
    // (those use their own swap mechanism). No hardcoded ordering —
    // purely positional within the current list.
    const reorderWidget = useCallback((srcId, tgtId) => {
        if (!srcId || !tgtId || srcId === tgtId) return;
        const all = [...currentWidgets];
        const srcIdx = all.findIndex((w) => w.id === srcId);
        const tgtIdx = all.findIndex((w) => w.id === tgtId);
        if (srcIdx < 0 || tgtIdx < 0) return;
        const [moved] = all.splice(srcIdx, 1);
        all.splice(tgtIdx, 0, moved);
        setDraftWidgets(all);
    }, [currentWidgets]);
    // iter-120 · Chart-card drill-down. Triggered by `dashboard:chart-drill`
    // window event fired from PrebuiltChartCard when the user clicks a
    // bar / slice / line point. We synthesize a stub KPI-shaped object
    // (the dialog already accepts `{ formula, title, ... }`) AND pass
    // a `filterPayload` pinning the clicked dimension value, so the
    // dialog opens with the per-group rows for that slice.
    const [drillDownChart, setDrillDownChart] = useState(null);  // { widget, label, seriesName }
    React.useEffect(() => {
        const onDrill = (e) => {
            if (rearranging) return;
            const d = e?.detail || {};
            if (d.dashboardId && d.dashboardId !== dashboard?.id) return;
            if (!d.widget) return;
            setDrillDownChart({ widget: d.widget, label: d.label, seriesName: d.seriesName });
        };
        window.addEventListener("dashboard:chart-drill", onDrill);
        return () => window.removeEventListener("dashboard:chart-drill", onDrill);
    }, [dashboard?.id, rearranging]);
    // iter-119aa (P0) — dataset-derived filterable fields. We fetch
    // /api/dashboards/{id}/filterable-fields once per dashboard load
    // and merge with `dashboard.filters` (template wins on collision).
    // Dynamic chips are gated behind a "Show more" toggle so the dock
    // doesn't get cluttered. Auto-clears on dashboard switch.
    const [dynamicFields, setDynamicFields] = useState([]);
    const [showAllDynamic, setShowAllDynamic] = useState(false);
    const DYNAMIC_VISIBLE_DEFAULT = 4;
    // already declared above
    const queryUrl = isPreview
        ? `/gallery/${dashboard?.id}/preview-query`
        : `/dashboards/${dashboard?.id}/joined-query`;
    const distinctUrl = isPreview
        ? `/gallery/${dashboard?.id}/distinct-preview`
        : `/dashboards/${dashboard?.id}/distinct`;
    const handleRegenerate = useCallback(async () => {
        // iter-119aa — Regenerate must bypass the joined-query cache
        // exactly once so users see truly-fresh data after they click.
        setBypassCacheOnce(true);
        // iter-120 · ALSO re-run the backend augmenter so newly-ingested
        // columns (channel, country, device, source, …) become chart
        // widgets on the dashboard instead of just re-fetching data
        // against the existing widget list. Augmenter is idempotent —
        // dedups by `augmentation_key` AND semantic `(metric × dim)`
        // pair — so spamming the button doesn't duplicate widgets.
        // Best-effort: a 404/410 from older deployments OR a dashboard
        // without a template_id (custom dashboards) silently falls
        // through to a plain data-refresh.
        let appended = 0;
        try {
            const r = await api.post(`/dashboards/${dashboard.id}/regenerate-from-template`);
            appended = r.data?.appended || 0;
            if (Array.isArray(r.data?.widgets) && onDashboardUpdated) {
                onDashboardUpdated({ ...dashboard, widgets: r.data.widgets });
            }
        } catch (e) {
            // 400 (no template id) is expected on custom dashboards —
            // fall through to data refresh. Other errors surface a
            // toast so SA can see what broke.
            const status = e?.response?.status;
            if (status && status !== 400 && status !== 410) {
                toast.error(e?.response?.data?.detail || "Regenerate failed");
            }
        }
        setRefreshKey((k) => k + 1);
        if (appended > 0) {
            toast.success(`Refreshed dashboard — ${appended} new widget${appended !== 1 ? "s" : ""} added`);
        } else {
            toast.success("Refreshing dashboard…");
        }
    }, [dashboard, onDashboardUpdated]);
    // Date-preset application — when user clicks "Last 30 days" we compute
    // the from/to ISO strings and feed them into the date_range filter so
    // /joined-query gets a `gte`/`lte` payload on the model's date field.
    const handleSetDatePreset = useCallback((key) => {
        setDatePreset(key);
        const dateFilter = (dashboard?.filters || []).find((f) => f.type === "date_range");
        if (!dateFilter) return;
        const fid = dateFilter.id || dateFilter.field;
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const isoDay = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
        let next = null;
        if (key === "all") {
            next = { from: null, to: null };
        } else if (key === "ytd") {
            next = { from: isoDay(new Date(today.getFullYear(), 0, 1)), to: isoDay(today) };
        } else {
            const days = { "7d": 7, "30d": 30, "90d": 90 }[key];
            if (days) {
                const f = new Date(today); f.setDate(f.getDate() - days + 1);
                next = { from: isoDay(f), to: isoDay(today) };
            }
        }
        if (next) {
            setFilterValues((prev) => ({ ...prev, [fid]: next }));
            setRefreshKey((k) => k + 1);
        }
    }, [dashboard?.filters]);
    const handleClearFilters = useCallback(() => {
        setFilterValues({});
        setDatePreset("all");
        setRefreshKey((k) => k + 1);
    }, []);

    // iter-119aa — decorate every joined-query body with:
    //   • _sa_scope payload (tenant_id / org_id) when an SA has picked
    //     a scope from the TopBar pill, so KPI/chart/table tiles all
    //     reflect the same SA-selected slice.
    //   • _nocache flag when the user just clicked Regenerate, so the
    //     backend skips the 60s TTL cache and re-runs the query against
    //     fresh `model_rows`.
    const _decorate = useCallback((body) => {
        const out = { ...body };
        if (scopeFilter?.isSA) {
            if (scopeFilter.tenantId) out.scope_tenant_id = scopeFilter.tenantId;
            if (scopeFilter.orgId) out.scope_org_id = scopeFilter.orgId;
        }
        if (bypassCacheOnce) out._nocache = true;
        return out;
    }, [scopeFilter?.isSA, scopeFilter?.tenantId, scopeFilter?.orgId, bypassCacheOnce]);

    // Reset the one-shot bypass after the effects have had a chance to fire.
    useEffect(() => {
        if (!bypassCacheOnce) return;
        const t = setTimeout(() => setBypassCacheOnce(false), 1500);
        return () => clearTimeout(t);
    }, [bypassCacheOnce, refreshKey]);

    // Build /joined-query `filters[]` payload from active filterValues.
    // Each filter spec is normalised to {model, field, op, value}. Always
    // includes the dashboard's persistent `where[]` rules — these are the
    // server-side WHERE clauses that bake permanent scope into the
    // dashboard (e.g. status NOT IN [removed]).
    const _filterPayload = useMemo(() => {
        const out = [];
        const bindings = dashboard?.data_model_bindings || [];
        const firstBinding = bindings[0];
        const rawSlug = typeof firstBinding === "string"
            ? firstBinding
            : (firstBinding?.model_id || "");
        const slug = rawSlug.includes(".") ? rawSlug.split(".").pop() : rawSlug;
        if (!slug) return out;
        // 1. Persistent `where[]` rules baked into the template.
        // iter-120 round-4 follow-up #19 — Skip entries with null/undefined/
        // empty-array values. These show up when the Sense AI Builder drafts
        // a template and leaves "filter not yet configured" markers in
        // `where[]` (e.g. `{op:"between", value:null}`). The backend
        // joined-query executor reads them strictly — `WHERE date BETWEEN
        // NULL AND NULL` matches ZERO rows, leaving every KPI/chart blank
        // even though sample data IS present. Same exclusion logic that
        // section 2 (UI filters) already applies, just hoisted up so the
        // user's perspective is consistent.
        for (const w of (dashboard?.where || [])) {
            if (!w || typeof w !== "object") continue;
            if (!w.field) continue;
            const v = w.value;
            if (v == null) continue;
            if (Array.isArray(v) && v.length === 0) continue;
            if (typeof v === "string" && v === "") continue;
            const field = String(w.field).includes(".") ? String(w.field).split(".").pop() : w.field;
            out.push({ model: w.model || slug, field, op: w.op || "eq", value: v });
        }
        // 2. User-controlled UI panel filters.
        for (const f of (dashboard?.filters || [])) {
            const v = filterValues[f.id || f.field];
            if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) continue;
            const fieldRaw = f.field || "";
            // `_source_kind` / `_source_id` are virtual root fields — keep
            // the underscore prefix; backend joined_query maps to root.
            const isVirtual = fieldRaw.startsWith("_");
            const field = isVirtual
                ? fieldRaw
                : (fieldRaw.includes(".") ? fieldRaw.split(".").pop() : fieldRaw);
            if (!field) continue;
            if ((f.type === "multi_select" || f.type === "multi_select_dynamic") && Array.isArray(v)) {
                out.push({ model: slug, field, op: "in", value: v });
            } else if (f.type === "date_range" && typeof v === "object") {
                if (v.from) out.push({ model: slug, field, op: "gte", value: v.from });
                if (v.to) out.push({ model: slug, field, op: "lte", value: v.to });
            } else if (f.type === "text") {
                out.push({ model: slug, field, op: "contains", value: String(v) });
            } else {
                out.push({ model: slug, field, op: "eq", value: v });
            }
        }
        // 3. iter-119aa (P0) — dataset-derived filters keyed by `dyn_<field>`.
        // Skip fields already pushed by template filters above (template wins).
        const takenFields = new Set(out.map((o) => o.field));
        for (const df of (dynamicFields || [])) {
            const leaf = (df.field || "").split(".").pop();
            if (!leaf || takenFields.has(leaf)) continue;
            const v = filterValues[`dyn_${leaf}`];
            if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) continue;
            if (df.kind === "date_range" && typeof v === "object") {
                if (v.from) out.push({ model: slug, field: leaf, op: "gte", value: v.from });
                if (v.to) out.push({ model: slug, field: leaf, op: "lte", value: v.to });
            } else if ((df.kind === "multi_select" || df.kind === "boolean") && Array.isArray(v)) {
                out.push({ model: slug, field: leaf, op: "in", value: v });
            } else if (df.kind === "search") {
                out.push({ model: slug, field: leaf, op: "contains", value: String(v) });
            } else if (df.kind === "range" && typeof v === "object") {
                if (v.from != null) out.push({ model: slug, field: leaf, op: "gte", value: v.from });
                if (v.to != null) out.push({ model: slug, field: leaf, op: "lte", value: v.to });
            }
        }
        return out;
    }, [dashboard?.data_model_bindings, dashboard?.filters, dashboard?.where, filterValues, dynamicFields]);

    useEffect(() => {
        let mounted = true;
        api.get(`/dashboards/${dashboard.id}/preferences`)
            .then((r) => { if (mounted) setPrefs(r.data); })
            .catch(() => { if (mounted) setPrefs({ slot_overrides: {} }); });
        return () => { mounted = false; };
    }, [dashboard.id]);

    // iter-119aa (P0) — load dataset-derived filterable fields ONCE per
    // dashboard so we can offer richer per-dataset filter chips beyond
    // the hardcoded date/source. Preview mode skips this (uses bundled
    // sample data, no model_rows to profile against).
    // iter-auto-filter-gate — dashboards cloned from a prebuilt template via
    // "Use this template" carry source_gallery_template_id. Their filter set
    // is curated by the template author; auto-discovery would inject every
    // dataset column as unwanted "AUTO" chips. Skip the fetch entirely so the
    // dock shows only the template's explicit filters.
    useEffect(() => {
        if (!dashboard?.id || isPreview) return;
        if (dashboard?.source_gallery_template_id) {
            setDynamicFields([]);
            return;
        }
        let mounted = true;
        api.get(`/dashboards/${dashboard.id}/filterable-fields`)
            .then((r) => { if (mounted) setDynamicFields(r.data?.fields || []); })
            .catch(() => { if (mounted) setDynamicFields([]); });
        return () => { mounted = false; };
    }, [dashboard?.id, dashboard?.source_gallery_template_id, isPreview]);

    // Resolve live KPI values via /joined-query — one round-trip per dashboard.
    // iter-65 fix: drop join_plan gate (executor reads data_model_joins lazily)
    // and accept both string-slug and {model_id} binding shapes.
    useEffect(() => {
        if (!dashboard?.id) return;
        const bindings = dashboard.data_model_bindings || [];
        // iter-121.82 — Upload-bound dashboards (Sense AI Builder against an
        // .xlsx / .csv) bind via `source.dataset_id` and ship EMPTY
        // `data_model_bindings`. The /joined-query path requires a canonical
        // model_id; without it the request can't be built. For these
        // dashboards we route per-widget through `/widgets/{id}/resolve`,
        // which already handles `dataset_rows` aggregation (see
        // dashboards_widgets_routes::resolve_widget line 638). Fires
        // BEFORE the legacy bindings branch so the strip never short-circuits
        // when only a dataset binding is present.
        const datasetId = dashboard?.source?.dataset_id;
        if (bindings.length === 0 && datasetId) {
            const kpiWidgets = resolvableKpiWidgets;
            if (kpiWidgets.length === 0) return;
            setLiveLoading(true);
            // Resolve each KPI independently — the backend resolver aggregates
            // up to 5000 rows from `dataset_rows` per call. Parallel
            // round-trips keep the strip snappy.
            (async () => {
                const next = {};
                await Promise.all(kpiWidgets.map(async (w) => {
                    try {
                        const r = await resolveWidget(api, dashboard.id, w, { filters: _filterPayload || {}, decorateFn: _decorate });
                        const v = r?.data?.value;
                        if (v != null) next[w.id] = v;
                    } catch { /* leave empty — tile shows em-dash */ }
                }));
                setLiveValues(next);
                setEmpty(Object.keys(next).length === 0);
                setLiveDeltas({});
                setLiveLoading(false);
            })();
            return;
        }
        if (bindings.length === 0) return;
        const widgets = resolvableKpiWidgets;
        if (widgets.length === 0) return;
        const select = [];
        const ratioPlan = {};
        const firstBinding = bindings[0];
        const rawSlug = typeof firstBinding === "string"
            ? firstBinding
            : (firstBinding?.model_id || "model.unknown");
        const modelSlug = rawSlug.includes(".") ? rawSlug.split(".").pop() : rawSlug;
        // The Sense AI Builder emits formulas with an optional model qualifier
        // (e.g. `SUM(c.spend)` where `c` is the campaign alias). The qualifier
        // is metadata for humans reading the JSON — not something the executor
        // needs — so we strip `<alias>.` before mapping to {model, field}.
        // Backend normaliser strips trailing `*100` and tags `unit:'%'` so we
        // never see the `*100` suffix here, but the regex tolerates it as a
        // belt-and-braces in case an older template skipped normalisation.
        // iter-119x.3: support multi-segment field paths produced by the
        // SenseAI Builder (e.g. `SUM(m.custom_metrics.clicks)`). The
        // capturing regex now accepts ANY number of segments and exposes
        // only the LAST segment as `field`. The backend joined-query
        // route applies the synonym table after this — so we send the
        // leaf node and let the server canonicalize.
        const _arg = "((?:[a-zA-Z_][a-zA-Z0-9_]*\\.)*)([a-zA-Z_][a-zA-Z0-9_]*)";
        const _trail = "(?:\\s*\\*\\s*\\d+(?:\\.\\d+)?)?";  // optional `*100` etc.
        const simpleRe = new RegExp(`^\\s*(SUM|AVG|MIN|MAX|COUNT|COUNT_DISTINCT)\\s*\\(\\s*${_arg}\\s*\\)${_trail}\\s*$`, "i");
        const countDistinctRe = new RegExp(`^\\s*COUNT\\s*\\(\\s*DISTINCT\\s+${_arg}\\s*\\)${_trail}\\s*$`, "i");
        const ratioRe = new RegExp(
            `^\\s*(SUM|AVG)\\s*\\(\\s*${_arg}\\s*\\)\\s*\\/\\s*(SUM|AVG|COUNT)\\s*\\(\\s*${_arg}\\s*\\)${_trail}\\s*$`,
            "i",
        );




        const filteredWidgets = [];
        const skippedWidgets = [];
        widgets.forEach((w) => {
            const f = (w.formula || "").trim();
            const wFilters = w.filters || w.where || [];
            
            // ISSUE-FIX: Push widgets with custom filters to filteredWidgets so they resolve individually with merged filters
            if (Array.isArray(wFilters) && wFilters.length > 0) {
                filteredWidgets.push(w);
                return;
            }
            
            const cond = extractConditionFromFormula(f);
            if (cond) {
                // ISSUE-FIX: When formula has no explicit measure, default field to '*' instead of 'id' so models without an 'id' column evaluate properly
                const mField = cond.measure ? (cond.measure.includes(".") ? cond.measure.split(".").pop() : cond.measure) : null;
                const cField = cond.cond.includes(" ") ? "*" : (cond.cond.includes(".") ? cond.cond.split(".").pop() : cond.cond);
                const aggName = cond.baseAgg === "count_distinct" ? "count_distinct_if" : cond.baseAgg + "_if";
                select.push({
                    model: modelSlug,
                    field: mField || (cond.baseAgg === "count" ? "*" : cField),
                    agg: aggName,
                    formula: f,
                    alias: `__kpi_${w.id}`,
                });
                return;
            }

            const cdMatch = countDistinctRe.exec(f);
            if (cdMatch) {
                select.push({ model: modelSlug, field: cdMatch[2], agg: "count_distinct", alias: `__kpi_${w.id}` });
                return;
            }
            const simple = simpleRe.exec(f);
            if (simple) {
                const aggName = simple[1].toLowerCase();
                select.push({ model: modelSlug, field: simple[3], agg: aggName, alias: `__kpi_${w.id}` });
                return;
            }
            const ratio = ratioRe.exec(f);
            if (ratio) {
                const numAlias = `__kpi_${w.id}_n`;
                const denomAlias = `__kpi_${w.id}_d`;
                select.push({ model: modelSlug, field: ratio[3], agg: ratio[1].toLowerCase(), alias: numAlias });
                select.push({ model: modelSlug, field: ratio[6], agg: ratio[4].toLowerCase(), alias: denomAlias });
                ratioPlan[w.id] = { num: numAlias, denom: denomAlias, scale: (w.unit === "%" ? 100 : 1) };
                return;
            }

            // ISSUE-FIX: Track skipped widgets that can't be parsed by client-side regexes (e.g. CASE WHEN, complex DATEDIFF)
            skippedWidgets.push(w);
        });
        if (select.length === 0 && filteredWidgets.length === 0 && skippedWidgets.length === 0) return;

        setLiveLoading(true);

        const promises = [];
        if (select.length > 0) {
            promises.push(api.post(queryUrl, _decorate({ select, filters: _filterPayload, limit: 1 })));
        } else {
            promises.push(Promise.resolve({ data: { rows: [] } }));
        }

        // Fetch filtered widgets independently
        filteredWidgets.forEach((w) => {
            const f = (w.formula || "").trim();
            const wFilters = w.filters || w.where || [];
            const mergedFilters = [...(_filterPayload || []), ...wFilters];
            let wSelect;
            
            const cond = extractConditionFromFormula(f);
            if (cond) {
                // ISSUE-FIX: When formula has no explicit measure, default field to '*' instead of 'id' so models without an 'id' column evaluate properly
                const mField = cond.measure ? (cond.measure.includes(".") ? cond.measure.split(".").pop() : cond.measure) : null;
                const cField = cond.cond.includes(" ") ? "*" : (cond.cond.includes(".") ? cond.cond.split(".").pop() : cond.cond);
                const aggName = cond.baseAgg === "count_distinct" ? "count_distinct_if" : cond.baseAgg + "_if";
                wSelect = [{
                    model: modelSlug,
                    field: mField || (cond.baseAgg === "count" ? "*" : cField),
                    agg: aggName,
                    formula: f,
                    alias: `__kpi_${w.id}`,
                }];
            }

            if (!wSelect) {
                const cdMatch = countDistinctRe.exec(f);
                const simple = simpleRe.exec(f);
                if (cdMatch) {
                    wSelect = [{ model: modelSlug, field: cdMatch[2], agg: "count_distinct", alias: `__kpi_${w.id}` }];
                } else {
                    const wField = simple ? simple[3] : "*";
                    const wAgg = simple ? simple[1].toLowerCase() : "count";
                    wSelect = [{ model: modelSlug, field: wField, agg: wAgg, alias: `__kpi_${w.id}` }];
                }
            }
            promises.push(
                api.post(queryUrl, _decorate({ select: wSelect, filters: mergedFilters, limit: 1 }))
                    .then((r) => ({ widgetId: w.id, value: (r.data?.rows || [])[0]?.[`__kpi_${w.id}`] }))
                    .catch(() => ({ widgetId: w.id, value: null }))
            );
        });

        // ISSUE-FIX: Resolve skipped widgets independently using the backend resolver (e.g. CASE WHEN, DATEDIFF, compound formulas)
        skippedWidgets.forEach((w) => {
            promises.push(
                api.post(`/dashboards/${dashboard.id}/widgets/${w.id}/resolve`, _decorate({ filters: _filterPayload || {} }))
                    .then((r) => ({ widgetId: w.id, value: r?.data?.value }))
                    .catch(() => ({ widgetId: w.id, value: null }))
            );
        });

        Promise.all(promises)
            .then(([mainRes, ...fwResults]) => {
                const row = (mainRes.data?.rows || [])[0] || {};
                const next = {};
                widgets.forEach((w) => {
                    const direct = row[`__kpi_${w.id}`];
                    if (direct != null) { next[w.id] = direct; return; }
                    const rp = ratioPlan[w.id];
                    if (rp) {
                        const num = row[rp.num];
                        const denom = row[rp.denom];
                        if (denom) next[w.id] = (num / denom) * rp.scale;
                    }
                });
                fwResults.forEach((res) => {
                    if (res && res.widgetId && res.value != null) {
                        next[res.widgetId] = res.value;
                    }
                });
                setLiveValues(next);
                setEmpty(Object.keys(next).length === 0);
                // iter-119aa (G2) — Period-over-period delta. Fire a second
                // identical query against the previous EQUAL-LENGTH date
                // range so each tile can show "↑12% vs prev period".
                // Skipped when there's no active date_range (no previous
                // slice is meaningfully comparable to "All time").
                const dateF = _filterPayload.find((f) => f.op === "gte");
                const dateT = _filterPayload.find((f) => f.op === "lte");
                if (dateF && dateT && dateF.field === dateT.field) {
                    const from = new Date(dateF.value);
                    const to = new Date(dateT.value);
                    const spanMs = Math.max(1, to.getTime() - from.getTime());
                    const prevTo = new Date(from.getTime() - 86400000); // day before "from"
                    const prevFrom = new Date(prevTo.getTime() - spanMs);
                    const isoDay = (d) => d.toISOString().slice(0, 10);
                    const prevFilters = _filterPayload.map((f) => {
                        if (f.field === dateF.field && f.op === "gte") return { ...f, value: isoDay(prevFrom) };
                        if (f.field === dateT.field && f.op === "lte") return { ...f, value: isoDay(prevTo) };
                        return f;
                    });
                    return api.post(queryUrl, _decorate({ select, filters: prevFilters, limit: 1 }))
                        .then((rp2) => {
                            const prevRow = (rp2.data?.rows || [])[0] || {};
                            const deltas = {};
                            const priorVals = {};
                            widgets.forEach((w) => {
                                const cur = next[w.id];
                                let prev;
                                const direct = prevRow[`__kpi_${w.id}`];
                                if (direct != null) {
                                    prev = direct;
                                } else {
                                    const rp = ratioPlan[w.id];
                                    if (rp) {
                                        const n = prevRow[rp.num], d2 = prevRow[rp.denom];
                                        if (d2) prev = (n / d2) * rp.scale;
                                    }
                                }
                                // iter-121.107 — Always remember the
                                // prior value (even when delta isn't
                                // computable, e.g. prev === 0) so the
                                // KPI tile footer shows "vs prior $0".
                                if (prev != null) priorVals[w.id] = prev;
                                if (cur == null || prev == null || prev === 0) return;
                                const pct = ((cur - prev) / Math.abs(prev)) * 100;
                                deltas[w.id] = Math.round(pct * 10) / 10;
                            });
                            setLiveDeltas(deltas);
                            setLivePriorValues(priorVals);
                        })
                        .catch(() => { setLiveDeltas({}); setLivePriorValues({}); });
                } else {
                    setLiveDeltas({});
                    setLivePriorValues({});
                }
            })
            .catch(() => { setEmpty(true); setLiveDeltas({}); setLivePriorValues({}); })
            .finally(() => setLiveLoading(false));
    }, [dashboard?.id, dashboard?.join_plan, dashboard?.data_model_bindings, dashboard?.source?.dataset_id, resolvableKpiWidgets, _filterPayload, refreshKey, _decorate, queryUrl]);

    // Resolve chart widgets (bar/line) via /joined-query. Same logic as KPIs:
    // formulas may be `SUM(c.spend)`-qualified; group_by may be `["c.start_at"]`
    // or explicit `x_field`. Prebuilt templates emit the qualified shape.
    useEffect(() => {
        if (!dashboard?.id) return;
        const _strip = (s) => {
            if (!s) return s;
            const i = String(s).lastIndexOf(".");
            return i >= 0 ? String(s).slice(i + 1) : String(s);
        };
        // Parse a chart formula into either a simple aggregate or a ratio
        // (SUM(a)/SUM(b) — common for ROAS, CTR, CPA on charts). Ratios
        // need TWO selects (num + denom) per row, then we divide client-side.
        // ISSUE-FIX: Support COUNT_DISTINCT and COUNT(DISTINCT ...) in chart formula parsing
        const _parseFormula = (f) => {
            if (!f) return null;
            const _arg = "((?:[a-zA-Z_][a-zA-Z0-9_]*\\.)*)([a-zA-Z_][a-zA-Z0-9_]*)";
            const _trail = "(?:\\s*\\*\\s*\\d+(?:\\.\\d+)?)?";
            const ratioRe = new RegExp(
                `^\\s*(SUM|AVG|COUNT_DISTINCT|COUNT)\\s*\\(\\s*${_arg}\\s*\\)\\s*\\/\\s*(SUM|AVG|COUNT_DISTINCT|COUNT)\\s*\\(\\s*${_arg}\\s*\\)${_trail}\\s*$`,
                "i",
            );
            const ratioM = ratioRe.exec(f);
            if (ratioM) {
                const trail = /\*\s*(\d+(?:\.\d+)?)\s*$/.exec(f);
                const scale = trail ? parseFloat(trail[1]) : 1;
                return {
                    type: "ratio",
                    numAgg: ratioM[1].toLowerCase(), numField: ratioM[3],
                    denomAgg: ratioM[4].toLowerCase(), denomField: ratioM[6],
                    scale,
                };
            }
            const countDistinctRe = new RegExp(`^\\s*COUNT\\s*\\(\\s*DISTINCT\\s+${_arg}\\s*\\)${_trail}\\s*$`, "i");
            const cdMatch = countDistinctRe.exec(f);
            if (cdMatch) {
                return { type: "simple", agg: "count_distinct", field: cdMatch[2] };
            }
            const simpleRe = new RegExp(`^\\s*(COUNT_DISTINCT|DISTINCT|SUM|AVG|MIN|MAX|COUNT)\\s*\\(\\s*${_arg}\\s*\\)${_trail}\\s*$`, "i");
            const m = simpleRe.exec(f);
            if (m) {
                const agg = m[1].toLowerCase() === "distinct" ? "count_distinct" : m[1].toLowerCase();
                return { type: "simple", agg, field: m[3] };
            }
            const cond = extractConditionFromFormula(f);
            if (cond) {
                return { type: "simple", agg: cond.baseAgg, field: cond.measure || "*" };
            }
            return null;
        };
        const charts = (dashboard.widgets || []).filter((w) => {
            if (w.chart_type !== "line" && w.chart_type !== "bar" && w.chart_type !== "horizontal_bar" && w.chart_type !== "donut" && w.chart_type !== "pie" && w.chart_type !== "geo_map" && w.chart_type !== "combo" && w.chart_type !== "composed" && w.chart_type !== "area") return false;
            const hasExplicit = w.x_field && w.y_field;
            const parsed = _parseFormula(w.formula);
            // group_by may arrive as ARRAY (`['c.start_at']`) OR as a STRING
            // (`'c.channel'`) when the LLM forgets the wrapping list.
            const groupRaw = Array.isArray(w.group_by)
                ? w.group_by[0]
                : (typeof w.group_by === "string" ? w.group_by : null);
            const hasSeries = Array.isArray(w.series) && w.series.length > 0
                && w.series.every((s) => s && typeof s.formula === "string" && s.formula.trim());
            // Either parsed shape (simple agg or ratio) or multi-series is eligible.
            return hasExplicit || (parsed && groupRaw) || (hasSeries && groupRaw);
        });
        if (charts.length === 0) return;
        const bindings = dashboard.data_model_bindings || [];
        // iter-121.82 — Upload-bound dashboards: route chart widgets through
        // per-widget /resolve, which handles `dataset_rows` aggregation
        // (group-by + simple aggregates). Without this branch every
        // chart card on an XLSX-backed dashboard renders blank.
        const datasetId = dashboard?.source?.dataset_id;
        if (bindings.length === 0 && datasetId) {
            (async () => {
                const next = {};
                await Promise.all(charts.map(async (w) => {
                    try {
                        const r = await resolveWidget(api, dashboard.id, w, {
                            filters: w.ignore_strip_filters ? {} : (_filterPayload || {}),
                            timeGrain,
                            decorateFn: _decorate,
                        });
                        const rd = r?.data || {};
                        // `/resolve` returns `{data: [...]}` for charts. The
                        // gallery chart card consumes `widget.data`, so we
                        // mirror that shape under `liveCharts[w.id].data`.
                        const data = Array.isArray(rd.data) ? rd.data
                            : Array.isArray(rd.rows) ? rd.rows : null;
                        if (data) {
                            next[w.id] = {
                                data,
                                geo_level: rd.geo_level,
                                geo_country_field: rd.geo_country_field,
                            };
                        }
                    } catch { /* leave empty */ }
                }));
                setLiveCharts((prev) => ({ ...prev, ...next }));
            })();
            return;
        }
        if (bindings.length === 0) return;
        const firstBinding = bindings[0];
        const rawSlug = typeof firstBinding === "string"
            ? firstBinding
            : (firstBinding?.model_id || "model.unknown");
        const modelSlug = rawSlug.includes(".") ? rawSlug.split(".").pop() : rawSlug;
        const collected = {};
        Promise.all(charts.map((w) => {
            const hasBreakdown = (w.dimensions && w.dimensions.length > 0) || 
                                 (w.group_by && w.group_by.length > 1) || 
                                 (w.options && w.options.stacked);
            const hasSeries = Array.isArray(w.series) && w.series.length > 0;
            if (w.chart_type === "combo" || w.chart_type === "composed" || hasBreakdown || hasSeries) {
                return resolveWidget(api, dashboard.id, w, {
                    filters: w.ignore_strip_filters ? {} : _filterPayload,
                    timeGrain,
                    decorateFn: _decorate,
                })
                .then((r) => {
                    const rd = r?.data || {};
                    collected[w.id] = rd;
                })
                .catch(() => { collected[w.id] = []; });
            }
            const parsed = _parseFormula(w.formula);
            const groupRaw = Array.isArray(w.group_by)
                ? w.group_by[0]
                : (typeof w.group_by === "string" ? w.group_by : null);
            const xField = w.x_field || _strip(groupRaw);
            const geoField = String(xField || "").toLowerCase();
            const isAdmin1Geo = w.chart_type === "geo_map"
                && (["state", "province", "region", "admin1", "admin_1"].includes(geoField)
                    || /(state|province|region|admin1|admin_1)/.test(geoField));
            // Build the select clause depending on whether the formula is a
            // simple aggregate, a ratio, or if it is a multi-series widget.
            let select; let isRatio = false; let scale = 1;
            if (hasSeries) {
                select = w.series.map((s, idx) => {
                    const sp = _parseFormula(s.formula);
                    if (sp && sp.type === "simple") {
                        // ISSUE-FIX: Forward conditional chart formulas with _if aggregate suffix and formula string
                        const cond = extractConditionFromFormula(s.formula);
                        if (cond) {
                            const aggName = cond.baseAgg === "count_distinct" ? "count_distinct_if" : (sp.agg.endsWith("_if") ? sp.agg : sp.agg + "_if");
                            return { model: modelSlug, field: sp.field, agg: aggName, formula: s.formula, alias: s.name };
                        }
                        return { model: modelSlug, field: sp.field, agg: sp.agg, alias: s.name };
                    }
                    return null;
                }).filter((s) => s !== null);
                if (select.length === 0) { collected[w.id] = []; return Promise.resolve(); }
            } else if (parsed && parsed.type === "ratio") {
                isRatio = true;
                scale = parsed.scale || 1;
                select = [
                    { model: modelSlug, field: parsed.numField, agg: parsed.numAgg, alias: "__num" },
                    { model: modelSlug, field: parsed.denomField, agg: parsed.denomAgg, alias: "__denom" },
                ];
            } else {
                const yField = w.y_field || (parsed && parsed.field);
                const agg = w.agg || (parsed && parsed.agg) || "sum";
                if (!yField) { collected[w.id] = []; return Promise.resolve(); }
                // ISSUE-FIX: Forward conditional chart formulas with _if aggregate suffix and formula string
                const cond = extractConditionFromFormula(w.formula);
                if (cond) {
                    const aggName = cond.baseAgg === "count_distinct" ? "count_distinct_if" : (agg.endsWith("_if") ? agg : agg + "_if");
                    select = [{ model: modelSlug, field: yField, agg: aggName, formula: w.formula, alias: "value" }];
                } else {
                    select = [{ model: modelSlug, field: yField, agg, alias: "value" }];
                }
            }
            const groupBy = [{ model: modelSlug, field: xField, alias: "label" }];
            const countryField = _strip(w.geo_country_field || w.config?.geo_country_field || "country");
            if (isAdmin1Geo && countryField && countryField !== xField) {
                groupBy.push({ model: modelSlug, field: countryField, alias: "country" });
            }
            // ISSUE-FIX: Dynamically resolve sorting order and fields based on widget configuration options.
            const isDateLike = (field) => {
                const f = String(field || "").toLowerCase();
                return /(date|month|year|quarter|day|start_at|created_at|occurred_at)/.test(f);
            };
            const isChrono = isDateLike(xField);

            const sb = w.sort_by || w.config?.sort_by;
            let sortAlias;
            if (sb) {
                if (sb === "label" || sb === w.x_field || sb === xField) {
                    sortAlias = "label";
                } else if (sb === "value" || sb === w.y_field) {
                    sortAlias = hasSeries ? select[0].alias : (isRatio ? "__num" : "value");
                } else if (hasSeries) {
                    const match = w.series.find(s => s.name === sb || s.column === sb);
                    sortAlias = match ? match.name : select[0].alias;
                } else {
                    sortAlias = "value";
                }
            } else {
                sortAlias = (isChrono || (!isChrono && w.chart_type !== "bar" && w.chart_type !== "donut" && w.chart_type !== "pie"))
                    ? "label"
                    : (hasSeries ? select[0].alias : (isRatio ? "__num" : "value"));
            }

            const sd = w.sort_dir || w.config?.sort_dir;
            let sortDir;
            if (sd) {
                sortDir = sd.toLowerCase().startsWith("asc") ? "asc" : "desc";
            } else if (isChrono) {
                sortDir = "asc";
            } else {
                if (sortAlias === "label") {
                    sortDir = "asc";
                } else {
                    sortDir = (w.title && /\b(bottom|worst|lowest)\b/i.test(w.title)) ? "asc" : "desc";
                }
            }

            // ISSUE-FIX: If we are sorting by value, we always query descending to retrieve the top (largest) values,
            // unless the user explicitly requested "bottom" or "lowest". We then sort the retrieved subset ascending
            // in the frontend. If we query ascending directly, MongoDB returns the bottom zero-valued rows.
            const isBottom = !!(w.title && /\b(bottom|worst|lowest)\b/i.test(w.title));
            let queryDir = sortDir;
            if (sortAlias !== "label") {
                queryDir = isBottom ? "asc" : "desc";
            }

            const limitN = parseInt(w.limit || w.config?.limit || w.top_n || w.config?.top_n, 10) || 100;

            const body = {
                select,
                group_by: groupBy,
                // iter-119ab (G5) — widgets can opt out of inheriting the
                // strip's filters via `widget.ignore_strip_filters`. Used
                // for sentinel KPIs like "Lifetime customers" that should
                // ignore the dashboard-wide date filter.
                // ISSUE-FIX: Merge widget-level filters for chart joined-queries
                filters: [
                    ...(w.ignore_strip_filters ? [] : (_filterPayload || [])),
                    ...(w.filters || w.where || []),
                ],
                order_by: [{ alias: sortAlias, dir: queryDir }],
                limit: limitN,
                // iter-121.179 — Marketing grain-role tag. Forwarded to
                // /joined-query so `model.marketing` widgets only sum
                // rows whose grain matches this role (base / device /
                // geo / demographic / …). When missing, the backend
                // auto-derives the role from `group_by[i].field`.
                dataset_role: w.dataset_role || w.config?.dataset_role || null,
            };
            return api.post(queryUrl, _decorate(body))
                .then((r) => {
                    let rows = (r.data?.rows || []);
                    // Materialise ratio → single `value` column so the
                    // chart card stays agnostic of formula shape.
                    if (isRatio) {
                        rows = rows
                            .filter((x) => x.label != null && x.__denom != null && x.__denom !== 0)
                            .map((x) => ({ label: x.label, country: x.country, value: (x.__num / x.__denom) * scale }));
                    } else if (hasSeries) {
                        rows = rows.filter((x) => x.label != null);
                    } else {
                        rows = rows.filter((x) => x.label != null && x.value != null);
                    }

                    // ISSUE-FIX: Apply final display sorting (ascending or descending) locally on the retrieved rows
                    rows.sort((a, b) => {
                        let va, vb;
                        if (sortAlias === "label") {
                            va = String(a.label || "");
                            vb = String(b.label || "");
                            return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
                        } else {
                            const key = (sortAlias === "__num") ? "value" : sortAlias;
                            va = Number(a[key] ?? a.value ?? 0);
                            vb = Number(b[key] ?? b.value ?? 0);
                            return sortDir === "asc" ? va - vb : vb - va;
                        }
                    });
                    const catLimit = (() => {
                        const explicit = w.limit || w.config?.limit || w.top_n || w.config?.top_n;
                        if (explicit) return explicit;
                        if (w.title) {
                            const m = /\b(top|bottom|worst|lowest)\s*(\d+)\b/i.exec(w.title);
                            if (m) return parseInt(m[2], 10);
                        }
                        return CFG.TOPN_BAR_CATEGORIES;
                    })();
                    const compareExtremes = !!(
                        w.compare_extremes || w.config?.compare_extremes ||
                        w.show_extremes || w.config?.show_extremes ||
                        w.top_bottom || w.config?.top_bottom ||
                        w.highest_and_lowest || w.config?.highest_and_lowest
                    );
                    
                    const isRowChrono = isDateLike(xField, rows[0]?.label);

                    if (compareExtremes && rows.length > 2) {
                        const sortedRows = [...rows].sort((a, b) => {
                            const va = Number(a.value) || 0;
                            const vb = Number(b.value) || 0;
                            return vb - va;
                        });
                        const limitN = parseInt(w.limit || w.config?.limit || w.top_n || w.config?.top_n, 10) || 0;
                        const isHighestLowest = w.title && /highest\s*(?:&|and)\s*lowest|best\s*(?:&|and)\s*worst/i.test(w.title);
                        const defaultEach = isHighestLowest ? 1 : (limitN ? Math.max(1, Math.floor(limitN / 2)) : 5);
                        const nTop = isHighestLowest ? 1 : Math.max(1, parseInt(w.extremes_top || w.config?.extremes_top || defaultEach, 10) || defaultEach);
                        const nBot = isHighestLowest ? 1 : Math.max(1, parseInt(w.extremes_bottom || w.config?.extremes_bottom || defaultEach, 10) || defaultEach);
                        const topPart = sortedRows.slice(0, nTop).map(r => ({ ...r, extreme: "top" }));
                        const botPart = sortedRows.slice(-nBot).map(r => ({ ...r, extreme: "bottom" }));
                        const seen = new Set();
                        const combined = [];
                        for (const r of [...topPart, ...botPart]) {
                            if (!seen.has(r.label)) {
                                seen.add(r.label);
                                combined.push(r);
                            }
                        }
                        collected[w.id] = isAdmin1Geo ? { data: combined, geo_level: "admin1", geo_country_field: countryField } : combined;
                    } else if (isRowChrono && (w.chart_type === "bar" || w.chart_type === "horizontal_bar" || w.chart_type === "donut" || w.chart_type === "pie")) {
                        const hasLimit = !!(w.limit || w.config?.limit || w.top_n || w.config?.top_n || (w.title && /top\s*(\d+)/i.test(w.title)));
                        let finalRows = rows;
                        if (hasLimit) {
                            // slice from the end to get the latest dates chronologically
                            finalRows = rows.slice(rows.length - catLimit);
                        } else if (rows.length > 60) {
                            // downsample to avoid cluttering
                            const step = Math.ceil(rows.length / 60);
                            const downsampled = [];
                            for (let i = 0; i < rows.length; i += step) {
                                downsampled.push(rows[i]);
                            }
                            finalRows = downsampled;
                        }
                        collected[w.id] = isAdmin1Geo ? { data: finalRows, geo_level: "admin1", geo_country_field: countryField } : finalRows;
                    } else if (!isRowChrono && (w.chart_type === "bar" || w.chart_type === "horizontal_bar" || w.chart_type === "donut" || w.chart_type === "pie") && rows.length > catLimit) {
                        const hasLimit = !!(w.limit || w.config?.limit || w.top_n || w.config?.top_n || (w.title && /\b(top|bottom|worst|lowest)\s*(\d+)\b/i.test(w.title)));
                        if (hasLimit) {
                            const limited = rows.slice(0, catLimit);
                            collected[w.id] = isAdmin1Geo ? { data: limited, geo_level: "admin1", geo_country_field: countryField } : limited;
                        } else {
                            const sliceLimit = Math.max(1, catLimit - 1);
                            const top = rows.slice(0, sliceLimit);
                            const restSum = rows.slice(sliceLimit).reduce((s, x) => s + (Number(x.value) || 0), 0);
                            if (restSum > 0) top.push({ label: `Other (${rows.length - sliceLimit})`, value: restSum });
                            collected[w.id] = isAdmin1Geo ? { data: top, geo_level: "admin1", geo_country_field: countryField } : top;
                        }
                    } else if ((w.chart_type === "line" || w.chart_type === "combo" || w.chart_type === "composed") && rows.length > CFG.TOPN_LINE_POINTS) {
                        // Chronological downsampling instead of naive slicing
                        const step = Math.ceil(rows.length / CFG.TOPN_LINE_POINTS);
                        const downsampled = [];
                        for (let i = 0; i < rows.length; i += step) {
                            downsampled.push(rows[i]);
                        }
                        collected[w.id] = isAdmin1Geo ? { data: downsampled, geo_level: "admin1", geo_country_field: countryField } : downsampled;
                    } else {
                        collected[w.id] = isAdmin1Geo ? { data: rows, geo_level: "admin1", geo_country_field: countryField } : rows;
                    }
                })
                .catch(() => { collected[w.id] = []; });
        })).then(() => setLiveCharts(collected));
    }, [dashboard?.id, dashboard?.widgets, dashboard?.data_model_bindings, dashboard?.source?.dataset_id, _filterPayload, refreshKey, _decorate, queryUrl]);

    // Resolve TABLE widgets — each column has its own SUM/AVG/COUNT formula
    // and we group by entity name (or first non-numeric, non-PII field).
    // Single /joined-query call per table, mapping all columns to alias slots.
    //
    // iter-121.129c — Multi-dimension table support. Previously the FE
    // collapsed `w.dimensions` to a single `entityField` (the first
    // dim), so a user asking the Builder for "table grouped by channel
    // AND device showing Spend, Clicks, Conversions" got a table with
    // ONE dimension column. The backend's `_tool_add_widget` preserved
    // every dimension on the widget, but the joined-query body only
    // sent the first as group_by. We now expand `w.dimensions` (and
    // legacy `w.group_by` lists) to N entity columns — multiple select
    // entries, multiple group_by entries — and pass them down as a
    // `dimensions` meta array so the renderer can show all of them
    // upfront.
    useEffect(() => {
        if (!dashboard?.id) return;
        const _strip = (s) => {
            if (!s) return s;
            const i = String(s).lastIndexOf(".");
            return i >= 0 ? String(s).slice(i + 1) : String(s);
        };
        // ISSUE-FIX: Tables now support COUNT DISTINCT, calculated columns, and all aggregation types with full parity to KPIs/charts.
        const _parseFormula = (f, colObj) => {
            if (!f && colObj && (colObj.field || colObj.column)) {
                const rawField = _strip(colObj.field || colObj.column);
                const rawAgg = String(colObj.agg || colObj.aggregation || colObj.function || "sum").toLowerCase().trim();
                const normAgg = (rawAgg === "distinct" || rawAgg === "count distinct" || rawAgg === "count(distinct)" || rawAgg === "distinct_count") ? "count_distinct" : rawAgg;
                return { type: "simple", agg: normAgg, field: rawField };
            }
            if (!f || typeof f !== "string") return null;
            const strF = f.trim();
            const _arg = "((?:[a-zA-Z_][a-zA-Z0-9_]*\\.)*)([a-zA-Z_][a-zA-Z0-9_]*)";
            const _trail = "(?:\\s*\\*\\s*\\d+(?:\\.\\d+)?)?";
            const ratioRe = new RegExp(
                `^\\s*(SUM|AVG|COUNT_DISTINCT|COUNT|MIN|MAX)\\s*\\(\\s*(?:DISTINCT\\s+)?${_arg}\\s*\\)\\s*\\/\\s*(SUM|AVG|COUNT_DISTINCT|COUNT|MIN|MAX)\\s*\\(\\s*(?:DISTINCT\\s+)?${_arg}\\s*\\)${_trail}\\s*$`,
                "i",
            );
            const ratioM = ratioRe.exec(strF);
            if (ratioM) {
                const trail = /\*\s*(\d+(?:\.\d+)?)\s*$/.exec(strF);
                const scale = trail ? parseFloat(trail[1]) : 1;
                const numAgg = ratioM[1].toLowerCase() === "distinct" ? "count_distinct" : ratioM[1].toLowerCase();
                const denomAgg = ratioM[4].toLowerCase() === "distinct" ? "count_distinct" : ratioM[4].toLowerCase();
                return {
                    type: "ratio",
                    numAgg, numField: ratioM[3],
                    denomAgg, denomField: ratioM[6],
                    scale,
                };
            }
            const countDistinctRe = new RegExp(`^\\s*COUNT\\s*\\(\\s*DISTINCT\\s+${_arg}\\s*\\)${_trail}\\s*$`, "i");
            const cdMatch = countDistinctRe.exec(strF);
            if (cdMatch) {
                return { type: "simple", agg: "count_distinct", field: cdMatch[2] };
            }
            const simpleRe = new RegExp(`^\\s*(COUNT_DISTINCT|DISTINCT|SUM|AVG|MIN|MAX|COUNT)\\s*\\(\\s*${_arg}\\s*\\)${_trail}\\s*$`, "i");
            const m = simpleRe.exec(strF);
            if (m) {
                const agg = (m[1].toLowerCase() === "distinct" || m[1].toLowerCase() === "count_distinct") ? "count_distinct" : m[1].toLowerCase();
                return { type: "simple", agg, field: m[3] };
            }
            const cond = extractConditionFromFormula(strF);
            if (cond) {
                const baseAgg = (cond.baseAgg === "distinct" || cond.baseAgg === "count_distinct") ? "count_distinct" : cond.baseAgg;
                return { type: "conditional", agg: baseAgg + "_if", field: cond.measure ? _strip(cond.measure) : "*", formula: strF };
            }
            return { type: "calculated", formula: strF };
        };
        const _normDimList = (w) => {
            // Canonical: w.dimensions (LLM's table dimensions).
            // Legacy: w.group_by (single string or list) or w.config.group_by.
            // Returns a list of stripped field names, e.g. ["channel", "device"].
            const raw = w.dimensions
                ?? w.group_by
                ?? (w.config && (w.config.dimensions || w.config.group_by))
                ?? null;
            if (!raw) return [];
            const arr = Array.isArray(raw) ? raw : [raw];
            return arr
                .map((d) => (typeof d === "string" ? _strip(d) : (d && typeof d === "object" ? _strip(d.field || d.name) : null)))
                .filter((d) => !!d);
        };
        const tables = (dashboard.widgets || []).filter((w) => w.chart_type === "table" && Array.isArray(w.columns) && w.columns.length > 0);
        if (tables.length === 0) return;
        const bindings = dashboard.data_model_bindings || [];
        // iter-121.82 — Upload-bound dashboards: route table widgets through
        // per-widget /resolve, which group-by's `dataset_rows` natively.
        const datasetId = dashboard?.source?.dataset_id;
        if (bindings.length === 0 && datasetId) {
            (async () => {
                const next = {};
                await Promise.all(tables.map(async (w) => {
                    try {
                        // ISSUE-FIX: Forward global dashboard filters to table resolve endpoint
                        const r = await resolveWidget(api, dashboard.id, w, {
                            filters: w.ignore_strip_filters ? {} : (_filterPayload || {}),
                            decorateFn: _decorate,
                            additionalParams: { limit: CFG.TABLE_ROW_LIMIT },
                        });
                        const rd = r?.data || {};
                        const rows = Array.isArray(rd.rows) ? rd.rows
                            : Array.isArray(rd.data) ? rd.data : [];
                        const columns = Array.isArray(rd.columns) ? rd.columns : [];
                        next[w.id] = {
                            rows,
                            columns,
                            entityLabel: rd.entityLabel,
                            offset: rows.length,
                            hasMore: rows.length === CFG.TABLE_ROW_LIMIT,
                        };
                    } catch { next[w.id] = { rows: [], columns: [] }; }
                }));
                setLiveTables((prev) => ({ ...prev, ...next }));
            })();
            return;
        }
        if (bindings.length === 0) return;
        const firstBinding = bindings[0];
        const rawSlug = typeof firstBinding === "string"
            ? firstBinding
            : (firstBinding?.model_id || "");
        const modelSlug = rawSlug.includes(".") ? rawSlug.split(".").pop() : rawSlug;
        if (!modelSlug) return;
        const collected = {};
        Promise.all(tables.map((w) => {
            // Parse each column's formula or config
            let hasCalculatedColumn = false;
            const select = [];
            const colMeta = [];
            const entityFromBareCols = [];
            (w.columns || []).forEach((c, i) => {
                const bareField = typeof c.formula === "string"
                    && /^[a-zA-Z_][a-zA-Z0-9_.\s-]*$/.test(c.formula.trim())
                    && !c.agg && !c.aggregation && !c.function;
                if (bareField) {
                    entityFromBareCols.push(_strip(c.formula.trim()));
                    return;
                }
                const parsed = _parseFormula(c.formula, c);
                const alias = `__col_${i}`;
                const label = c.label || c.name || (parsed?.field) || `Column ${i + 1}`;
                if (parsed) {
                    if (parsed.type === "ratio") {
                        select.push({ model: modelSlug, field: parsed.numField, agg: parsed.numAgg, alias: `${alias}_num` });
                        select.push({ model: modelSlug, field: parsed.denomField, agg: parsed.denomAgg, alias: `${alias}_denom` });
                        colMeta.push({
                            alias,
                            label,
                            type: "ratio",
                            numAlias: `${alias}_num`,
                            denomAlias: `${alias}_denom`,
                            scale: parsed.scale || 1,
                            format: c.format,
                        });
                    } else if (parsed.type === "conditional") {
                        select.push({ model: modelSlug, field: parsed.field, agg: parsed.agg, formula: parsed.formula, alias });
                        colMeta.push({
                            alias,
                            label,
                            type: "simple",
                            format: c.format,
                        });
                    } else if (parsed.type === "simple") {
                        select.push({ model: modelSlug, field: parsed.field, agg: parsed.agg, alias });
                        colMeta.push({
                            alias,
                            label,
                            type: "simple",
                            format: c.format,
                        });
                    } else if (parsed.type === "calculated") {
                        hasCalculatedColumn = true;
                        select.push({ model: modelSlug, field: "*", agg: "count", formula: parsed.formula, alias });
                        colMeta.push({
                            alias,
                            label,
                            type: "calculated",
                            formula: parsed.formula,
                            format: c.format,
                        });
                    }
                } else if (c.formula && /^[a-zA-Z_][a-zA-Z0-9_]*\.[a-zA-Z_][a-zA-Z0-9_]*$|^[a-zA-Z_][a-zA-Z0-9_]*$/.test(c.formula.trim())) {
                    entityFromBareCols.push(_strip(c.formula.trim()));
                } else {
                    colMeta.push({
                        alias,
                        label,
                        type: "simple",
                        format: c.format,
                    });
                }
            });
            // iter-121.129c — Build the canonical dimensions list.
            let dimFields = _normDimList(w);
            if (dimFields.length === 0 && entityFromBareCols.length > 0) {
                dimFields = entityFromBareCols;
            }
            if (dimFields.length === 0) dimFields = ["name"];
            const dimMeta = dimFields.map((f, i) => ({
                alias: `__entity_${i}`,
                field: f,
                label: f,
            }));
            if (colMeta.length === 0) {
                collected[w.id] = { rows: [], columns: [], dimensions: dimMeta };
                return Promise.resolve();
            }

            if (hasCalculatedColumn) {
                return resolveWidget(api, dashboard.id, w, {
                    filters: w.ignore_strip_filters ? {} : (_filterPayload || {}),
                    decorateFn: _decorate,
                    additionalParams: { limit: CFG.TABLE_ROW_LIMIT },
                })
                .then((r) => {
                    const rd = r?.data || {};
                    const rawRows = Array.isArray(rd.rows) ? rd.rows : [];
                    const mappedRows = rawRows.map((rObj) => {
                        const row = {};
                        dimMeta.forEach((d) => { row[d.alias] = rObj[d.field] ?? rObj[d.label] ?? ""; });
                        colMeta.forEach((c) => { row[c.alias] = rObj[c.label] ?? rObj[c.alias] ?? ""; });
                        return row;
                    });
                    const grandTotal = {};
                    if (rd.grand_total) {
                        colMeta.forEach((c) => {
                            if (rd.grand_total[c.label] !== undefined) grandTotal[c.alias] = rd.grand_total[c.label];
                        });
                    }
                    collected[w.id] = {
                        rows: mappedRows,
                        columns: colMeta,
                        dimensions: dimMeta,
                        offset: mappedRows.length,
                        hasMore: rawRows.length === CFG.TABLE_ROW_LIMIT,
                        entityLabel: dimMeta[0]?.label || dimFields[0],
                        grand_total: grandTotal,
                    };
                })
                .catch(() => {
                    collected[w.id] = { rows: [], columns: colMeta, dimensions: dimMeta, grand_total: {} };
                });
            }

            const body = {
                select: [
                    ...dimMeta.map((d) => ({ model: modelSlug, field: d.field, alias: d.alias })),
                    ...select,
                ],
                group_by: dimMeta.map((d) => ({ model: modelSlug, field: d.field, alias: d.alias })),
                order_by: [{ alias: select[0]?.alias || dimMeta[0]?.alias, dir: "desc" }],
                // iter-119ab (G5) — see chart-widget body above for rationale.
                // ISSUE-FIX: Include widget-level filters for table queries
                filters: [
                    ...(w.ignore_strip_filters ? [] : (_filterPayload || [])),
                    ...(w.filters || w.where || []),
                ],
                limit: CFG.TABLE_ROW_LIMIT,
                // iter-121.179 — Marketing grain-role tag (see chart body).
                dataset_role: w.dataset_role || w.config?.dataset_role || null,
                keep_zeroes: true,
            };
            const dataPromise = api.post(queryUrl, _decorate(body));
            const totalBody = {
                select,
                group_by: dimMeta.map((d) => ({ model: modelSlug, field: d.field, alias: d.alias })),
                // ISSUE-FIX: Include widget-level filters for table queries
                filters: [
                    ...(w.ignore_strip_filters ? [] : (_filterPayload || [])),
                    ...(w.filters || w.where || []),
                ],
                limit: CFG.TABLE_TOTAL_LIMIT,
                dataset_role: w.dataset_role || w.config?.dataset_role || null,
                keep_zeroes: true,
            };
            const totalPromise = api.post(queryUrl, _decorate(totalBody)).catch(() => null);

            return Promise.all([dataPromise, totalPromise])
                .then(([r, rTotal]) => {
                    const rawRows = r.data?.rows || [];
                    const processedRows = rawRows.map((row) => {
                        const newRow = { ...row };
                        colMeta.forEach((col) => {
                            if (col.type === "ratio") {
                                const num = _parseNumeric(row[col.numAlias]);
                                const denom = _parseNumeric(row[col.denomAlias]);
                                newRow[col.alias] = denom ? (num / denom) * col.scale : 0;
                            }
                        });
                        return newRow;
                    });
                    const rows = processedRows;
                    
                    const totalRow = {};
                    for (const totalSourceRow of rTotal?.data?.rows || []) {
                        colMeta.forEach((col) => {
                            const numerator = _parseNumeric(totalSourceRow[col.numAlias]);
                            const denominator = _parseNumeric(totalSourceRow[col.denomAlias]);
                            const value = col.type === "ratio"
                                ? (denominator ? (numerator / denominator) * col.scale : 0)
                                : _parseNumeric(totalSourceRow[col.alias]);
                            totalRow[col.alias] = (totalRow[col.alias] || 0) + value;
                        });
                    }

                    collected[w.id] = {
                        rows,
                        columns: colMeta,
                        dimensions: dimMeta,
                        offset: rows.length,
                        hasMore: rows.length === CFG.TABLE_ROW_LIMIT,
                        // Legacy single-entity API consumers still
                        // read these — populate them from the first
                        // dimension so older renderers keep working.
                        entityLabel: dimMeta[0]?.label || dimFields[0],
                        grand_total: totalRow,
                    };
                })
                .catch(async () => {
                    // Fallback to backend resolver if joined-query returns an error
                    try {
                        const r = await resolveWidget(api, dashboard.id, w, {
                            filters: w.ignore_strip_filters ? {} : (_filterPayload || {}),
                            decorateFn: _decorate,
                            additionalParams: { limit: CFG.TABLE_ROW_LIMIT },
                        });
                        const rd = r?.data || {};
                        const rawRows = Array.isArray(rd.rows) ? rd.rows : [];
                        const mappedRows = rawRows.map((rObj) => {
                            const row = {};
                            dimMeta.forEach((d) => { row[d.alias] = rObj[d.field] ?? rObj[d.label] ?? ""; });
                            colMeta.forEach((c) => { row[c.alias] = rObj[c.label] ?? rObj[c.alias] ?? ""; });
                            return row;
                        });
                        const grandTotal = {};
                        if (rd.grand_total) {
                            colMeta.forEach((c) => {
                                if (rd.grand_total[c.label] !== undefined) grandTotal[c.alias] = rd.grand_total[c.label];
                            });
                        }
                        collected[w.id] = {
                            rows: mappedRows,
                            columns: colMeta,
                            dimensions: dimMeta,
                            offset: mappedRows.length,
                            hasMore: rawRows.length === CFG.TABLE_ROW_LIMIT,
                            entityLabel: dimMeta[0]?.label || dimFields[0],
                            grand_total: grandTotal,
                        };
                    } catch {
                        collected[w.id] = {
                            rows: [],
                            columns: colMeta,
                            dimensions: dimMeta,
                            entityLabel: dimMeta[0]?.label || dimFields[0],
                            grand_total: {},
                        };
                    }
                });
        })).then(() => setLiveTables(collected));
    }, [dashboard?.id, dashboard?.widgets, dashboard?.data_model_bindings, dashboard?.source?.dataset_id, _filterPayload, refreshKey, _decorate, queryUrl]);
    const loadMoreTableRows = useCallback(async (w) => {
        if (!dashboard?.id || !w?.id) return;
        const current = liveTables[w.id] || {};
        if (!current.hasMore || tableLoadingMore[w.id]) return;
        const offset = Array.isArray(current.rows) ? current.rows.length : 0;
        setTableLoadingMore((prev) => ({ ...prev, [w.id]: true }));
        try {
            const datasetId = dashboard?.source?.dataset_id;
            const bindings = dashboard.data_model_bindings || [];
            let nextRows = [];
            let nextMeta = {};
            if (bindings.length === 0 && datasetId) {
                // ISSUE-FIX: Forward global dashboard filters to table resolve endpoint
                const r = await resolveWidget(api, dashboard.id, w, {
                    filters: w.ignore_strip_filters ? {} : (_filterPayload || {}),
                    decorateFn: _decorate,
                    additionalParams: { limit: CFG.TABLE_ROW_LIMIT, offset },
                });
                const rd = r?.data || {};
                nextRows = Array.isArray(rd.rows) ? rd.rows
                    : Array.isArray(rd.data) ? rd.data : [];
                nextMeta = {
                    columns: Array.isArray(rd.columns) ? rd.columns : current.columns || [],
                    entityLabel: rd.entityLabel || current.entityLabel,
                };
            } else if (bindings.length > 0) {
                const _strip = (s) => {
                    if (!s) return s;
                    const i = String(s).lastIndexOf(".");
                    return i >= 0 ? String(s).slice(i + 1) : String(s);
                };
                // ISSUE-FIX: Support COUNT_DISTINCT, calculated columns, and object dimensions in loadMoreTableRows
                const _parseFormula = (f, colObj) => {
                    if (!f && colObj && (colObj.field || colObj.column)) {
                        const rawField = _strip(colObj.field || colObj.column);
                        const rawAgg = String(colObj.agg || colObj.aggregation || colObj.function || "sum").toLowerCase().trim();
                        const normAgg = (rawAgg === "distinct" || rawAgg === "count distinct" || rawAgg === "count(distinct)" || rawAgg === "distinct_count") ? "count_distinct" : rawAgg;
                        return { type: "simple", agg: normAgg, field: rawField };
                    }
                    if (!f || typeof f !== "string") return null;
                    const strF = f.trim();
                    const _arg = "((?:[a-zA-Z_][a-zA-Z0-9_]*\\.)*)([a-zA-Z_][a-zA-Z0-9_]*)";
                    const _trail = "(?:\\s*\\*\\s*\\d+(?:\\.\\d+)?)?";
                    const ratioRe = new RegExp(
                        `^\\s*(SUM|AVG|COUNT_DISTINCT|COUNT|MIN|MAX)\\s*\\(\\s*(?:DISTINCT\\s+)?${_arg}\\s*\\)\\s*\\/\\s*(SUM|AVG|COUNT_DISTINCT|COUNT|MIN|MAX)\\s*\\(\\s*(?:DISTINCT\\s+)?${_arg}\\s*\\)${_trail}\\s*$`,
                        "i",
                    );
                    const ratioM = ratioRe.exec(strF);
                    if (ratioM) {
                        const trail = /\*\s*(\d+(?:\.\d+)?)\s*$/.exec(strF);
                        const scale = trail ? parseFloat(trail[1]) : 1;
                        const numAgg = ratioM[1].toLowerCase() === "distinct" ? "count_distinct" : ratioM[1].toLowerCase();
                        const denomAgg = ratioM[4].toLowerCase() === "distinct" ? "count_distinct" : ratioM[4].toLowerCase();
                        return {
                            type: "ratio",
                            numAgg, numField: ratioM[3],
                            denomAgg, denomField: ratioM[6],
                            scale,
                        };
                    }
                    const countDistinctRe = new RegExp(`^\\s*COUNT\\s*\\(\\s*DISTINCT\\s+${_arg}\\s*\\)${_trail}\\s*$`, "i");
                    const cdMatch = countDistinctRe.exec(strF);
                    if (cdMatch) {
                        return { type: "simple", agg: "count_distinct", field: cdMatch[2] };
                    }
                    const simpleRe = new RegExp(`^\\s*(COUNT_DISTINCT|DISTINCT|SUM|AVG|MIN|MAX|COUNT)\\s*\\(\\s*${_arg}\\s*\\)${_trail}\\s*$`, "i");
                    const m = simpleRe.exec(strF);
                    if (m) {
                        const agg = (m[1].toLowerCase() === "distinct" || m[1].toLowerCase() === "count_distinct") ? "count_distinct" : m[1].toLowerCase();
                        return { type: "simple", agg, field: m[3] };
                    }
                    const cond = extractConditionFromFormula(strF);
                    if (cond) {
                        const baseAgg = (cond.baseAgg === "distinct" || cond.baseAgg === "count_distinct") ? "count_distinct" : cond.baseAgg;
                        return { type: "conditional", agg: baseAgg + "_if", field: cond.measure ? _strip(cond.measure) : "*", formula: strF };
                    }
                    return { type: "calculated", formula: strF };
                };
                const _normDimList = (widget) => {
                    const raw = widget.dimensions
                        ?? widget.group_by
                        ?? (widget.config && (widget.config.dimensions || widget.config.group_by))
                        ?? null;
                    if (!raw) return [];
                    const arr = Array.isArray(raw) ? raw : [raw];
                    return arr
                        .map((d) => (typeof d === "string" ? _strip(d) : (d && typeof d === "object" ? _strip(d.field || d.name) : null)))
                        .filter((d) => !!d);
                };
                const firstBinding = bindings[0];
                const rawSlug = typeof firstBinding === "string"
                    ? firstBinding
                    : (firstBinding?.model_id || "");
                const modelSlug = rawSlug.includes(".") ? rawSlug.split(".").pop() : rawSlug;
                if (!modelSlug) return;
                let hasCalculatedColumn = false;
                const select = [];
                const colMeta = [];
                const entityFromBareCols = [];
                (w.columns || []).forEach((c, i) => {
                    const bareField = typeof c.formula === "string"
                        && /^[a-zA-Z_][a-zA-Z0-9_.\s-]*$/.test(c.formula.trim())
                        && !c.agg && !c.aggregation && !c.function;
                    if (bareField) {
                        entityFromBareCols.push(_strip(c.formula.trim()));
                        return;
                    }
                    const parsed = _parseFormula(c.formula, c);
                    const alias = `__col_${i}`;
                    const label = c.label || c.name || (parsed?.field) || `Column ${i + 1}`;
                    if (parsed) {
                        if (parsed.type === "ratio") {
                            select.push({ model: modelSlug, field: parsed.numField, agg: parsed.numAgg, alias: `${alias}_num` });
                            select.push({ model: modelSlug, field: parsed.denomField, agg: parsed.denomAgg, alias: `${alias}_denom` });
                            colMeta.push({
                                alias,
                                label,
                                type: "ratio",
                                numAlias: `${alias}_num`,
                                denomAlias: `${alias}_denom`,
                                scale: parsed.scale || 1,
                                format: c.format,
                            });
                        } else if (parsed.type === "conditional") {
                            select.push({ model: modelSlug, field: parsed.field, agg: parsed.agg, formula: parsed.formula, alias });
                            colMeta.push({
                                alias,
                                label,
                                type: "simple",
                                format: c.format,
                            });
                        } else if (parsed.type === "simple") {
                            select.push({ model: modelSlug, field: parsed.field, agg: parsed.agg, alias });
                            colMeta.push({
                                alias,
                                label,
                                type: "simple",
                                format: c.format,
                            });
                        } else if (parsed.type === "calculated") {
                            hasCalculatedColumn = true;
                            select.push({ model: modelSlug, field: "*", agg: "count", formula: parsed.formula, alias });
                            colMeta.push({
                                alias,
                                label,
                                type: "calculated",
                                formula: parsed.formula,
                                format: c.format,
                            });
                        }
                    } else if (c.formula && /^[a-zA-Z_][a-zA-Z0-9_]*\.[a-zA-Z_][a-zA-Z0-9_]*$|^[a-zA-Z_][a-zA-Z0-9_]*$/.test(c.formula.trim())) {
                        entityFromBareCols.push(_strip(c.formula.trim()));
                    } else {
                        colMeta.push({
                            alias,
                            label,
                            type: "simple",
                            format: c.format,
                        });
                    }
                });
                let dimFields = _normDimList(w);
                if (dimFields.length === 0 && entityFromBareCols.length > 0) dimFields = entityFromBareCols;
                if (dimFields.length === 0) dimFields = ["name"];
                const dimMeta = dimFields.map((f, i) => ({
                    alias: `__entity_${i}`,
                    field: f,
                    label: f,
                }));
                if (colMeta.length === 0) return;

                if (hasCalculatedColumn) {
                    const r = await resolveWidget(api, dashboard.id, w, {
                        filters: w.ignore_strip_filters ? {} : (_filterPayload || {}),
                        decorateFn: _decorate,
                        additionalParams: { limit: CFG.TABLE_ROW_LIMIT, offset },
                    });
                    const rd = r?.data || {};
                    const rawRows = Array.isArray(rd.rows) ? rd.rows : [];
                    nextRows = rawRows.map((rObj) => {
                        const row = {};
                        dimMeta.forEach((d) => { row[d.alias] = rObj[d.field] ?? rObj[d.label] ?? ""; });
                        colMeta.forEach((c) => { row[c.alias] = rObj[c.label] ?? rObj[c.alias] ?? ""; });
                        return row;
                    });
                    nextMeta = {
                        columns: colMeta,
                        dimensions: dimMeta,
                        entityLabel: dimMeta[0]?.label || dimFields[0],
                    };
                } else {
                    const body = {
                        select: [
                            ...dimMeta.map((d) => ({ model: modelSlug, field: d.field, alias: d.alias })),
                            ...select,
                        ],
                        group_by: dimMeta.map((d) => ({ model: modelSlug, field: d.field, alias: d.alias })),
                        order_by: [{ alias: select[0]?.alias || dimMeta[0]?.alias, dir: "desc" }],
                        // ISSUE-FIX: Include widget-level filters for table queries
                        filters: [
                            ...(w.ignore_strip_filters ? [] : (_filterPayload || [])),
                            ...(w.filters || w.where || []),
                        ],
                        limit: CFG.TABLE_ROW_LIMIT,
                        offset,
                        // iter-121.179 — Marketing grain-role tag.
                        dataset_role: w.dataset_role || w.config?.dataset_role || null,
                        keep_zeroes: true,
                    };
                    try {
                        const r = await api.post(queryUrl, _decorate(body));
                        const rawRows = r.data?.rows || [];
                        nextRows = rawRows.map((row) => {
                            const newRow = { ...row };
                            colMeta.forEach((col) => {
                                if (col.type === "ratio") {
                                    const num = _parseNumeric(row[col.numAlias]);
                                    const denom = _parseNumeric(row[col.denomAlias]);
                                    newRow[col.alias] = denom ? (num / denom) * col.scale : 0;
                                }
                            });
                            return newRow;
                        });
                        nextMeta = {
                            columns: colMeta,
                            dimensions: dimMeta,
                            entityLabel: dimMeta[0]?.label || dimFields[0],
                        };
                    } catch {
                        const r = await resolveWidget(api, dashboard.id, w, {
                            filters: w.ignore_strip_filters ? {} : (_filterPayload || {}),
                            decorateFn: _decorate,
                            additionalParams: { limit: CFG.TABLE_ROW_LIMIT, offset },
                        });
                        const rd = r?.data || {};
                        const rawRows = Array.isArray(rd.rows) ? rd.rows : [];
                        nextRows = rawRows.map((rObj) => {
                            const row = {};
                            dimMeta.forEach((d) => { row[d.alias] = rObj[d.field] ?? rObj[d.label] ?? ""; });
                            colMeta.forEach((c) => { row[c.alias] = rObj[c.label] ?? rObj[c.alias] ?? ""; });
                            return row;
                        });
                        nextMeta = {
                            columns: colMeta,
                            dimensions: dimMeta,
                            entityLabel: dimMeta[0]?.label || dimFields[0],
                        };
                    }
                }
            }
            setLiveTables((prev) => {
                const existing = prev[w.id] || {};
                const existingRows = Array.isArray(existing.rows) ? existing.rows : [];
                const mergedRows = [...existingRows, ...nextRows];
                return {
                    ...prev,
                    [w.id]: {
                        ...existing,
                        ...nextMeta,
                        rows: mergedRows,
                        offset: mergedRows.length,
                        hasMore: nextRows.length === CFG.TABLE_ROW_LIMIT,
                    },
                };
            });
        } catch {
            setLiveTables((prev) => ({
                ...prev,
                [w.id]: { ...(prev[w.id] || {}), hasMore: false },
            }));
        } finally {
            setTableLoadingMore((prev) => ({ ...prev, [w.id]: false }));
        }
    }, [dashboard, liveTables, tableLoadingMore, _filterPayload, _decorate, queryUrl]);
    const widgetsById = useMemo(() => {
        const m = {};
        currentWidgets.forEach((w) => { if (w?.id) m[w.id] = w; });
        return m;
    }, [currentWidgets]);
    const _norm = useCallback((k) => {
        if (typeof k === "string") return widgetsById[k] || { id: k, title: k };
        if (k && typeof k === "object") {
            return widgetsById[k.id] ? { ...widgetsById[k.id], ...k } : k;
        }
        return {};
    }, [widgetsById]);
    // iter-69.4: include EVERY KPI widget in the strip (locked + pool +
    // unmarked KPI widgets). Without this, KPIs the LLM emits but doesn't
    // explicitly add to `locked_kpis` get hidden. They're visually identical
    // to locked ones so the user expects to see them. Locked status only
    // affects swap-pool eligibility, not visibility.
    //
    // No hardcoded cap — the LLM decides per-dashboard how many KPIs belong
    // as primary (iter-72). Previously
    const lockedKpis = useMemo(() => {
        const explicit = (dashboard.locked_kpis || []).map(_norm).filter((k) => k && k.id);
        if (explicit.length > 0) return explicit;
        const poolIds = new Set((dashboard.kpi_pool || []).map((k) => (typeof k === "string" ? k : k?.id)).filter(Boolean));
        return currentWidgets
            .filter((w) => w && (w.chart_type === "kpi" || w.chart_type === "kpi_card") && !poolIds.has(w.id))
            .map((w) => ({ ...w, _orphan: true }));
    }, [dashboard.locked_kpis, dashboard.kpi_pool, currentWidgets, _norm]);
    const kpiPool = useMemo(() => (dashboard.kpi_pool || []).map(_norm), [dashboard.kpi_pool, _norm]);

    // Per-user preferences: ordering, sizes, hidden. Fall back to defaults so
    // a first-render dashboard (no prefs doc) looks identical to today.
    const hiddenIds = useMemo(() => new Set(prefs?.hidden_kpis || []), [prefs]);
    const kpiSizes = useMemo(() => {
        if (rearranging && draftKpiSizes) return draftKpiSizes;
        const out = { ...(prefs?.kpi_sizes || {}) };
        lockedKpis.forEach((k) => {
            if (k.id && k.size && !out[k.id]) {
                out[k.id] = k.size;
            }
        });
        return out;
    }, [rearranging, draftKpiSizes, prefs?.kpi_sizes, lockedKpis]);
    // Effective (ordered + visible) KPI list for the strip.
    const effectiveKpis = useMemo(() => {
        const byId = Object.fromEntries(lockedKpis.map((k) => [k.id, k]));
        const order = Array.isArray(currentKpiOrder) && currentKpiOrder.length > 0
            ? currentKpiOrder
            : lockedKpis.map((k) => k.id);
        const seen = new Set();
        const out = [];
        // Honour ordering first.
        for (const id of order) {
            if (hiddenIds.has(id)) continue;
            if (seen.has(id)) continue;
            if (byId[id]) { out.push(byId[id]); seen.add(id); }
        }
        // Any KPIs added since the user's order was saved append at the end.
        for (const k of lockedKpis) {
            if (hiddenIds.has(k.id)) continue;
            if (seen.has(k.id)) continue;
            out.push(k); seen.add(k.id);
        }
        return out;
    }, [lockedKpis, currentKpiOrder, hiddenIds]);

    const slotKpi = (idx) => {
        const k = effectiveKpis[idx];
        if (!k) return null;
        const overrideId = prefs?.slot_overrides?.[String(idx)] || prefs?.slot_overrides?.[idx];
        if (overrideId) {
            const found = [...lockedKpis, ...kpiPool].find((x) => x.id === overrideId);
            if (found) return { ...found, _overridden: true };
        }
        return k;
    };

    const savePreferences = async (patch) => {
        try {
            const r = await api.patch(`/dashboards/${dashboard.id}/preferences`, patch);
            setPrefs(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Preference save failed");
        }
    };

    // Remove KPI from the strip — it stays in the dashboard's widget pool so
    // the user can restore it from the pool drawer. Never mutates the shared
    // dashboard doc.
    const removeFromStrip = (kpiId) => {
        const next = Array.from(new Set([...(prefs?.hidden_kpis || []), kpiId]));
        savePreferences({ hidden_kpis: next });
        toast.success("KPI hidden — restore from the Pool drawer");
    };
    const restoreToStrip = (kpiId) => {
        const next = (prefs?.hidden_kpis || []).filter((x) => x !== kpiId);
        savePreferences({ hidden_kpis: next });
        toast.success("KPI restored");
    };
    // iter-121.132 — Per-user hide for non-KPI widgets (chart/table/map).
    // Same semantics as removeFromStrip: writes to prefs.hidden_widgets,
    // reversible from the pool. NEVER mutates the shared template.
    const hideWidgetForUser = useCallback((widgetId) => {
        if (!widgetId) return;
        const next = Array.from(new Set([...(prefs?.hidden_widgets || []), widgetId]));
        savePreferences({ hidden_widgets: next });
        toast.success("Widget hidden — restore from the Widget Pool");
        // savePreferences is stable across renders for the savePreferences
        // function shape we use here; depending on prefs is enough.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefs]);
    const restoreWidgetForUser = useCallback((widgetId) => {
        if (!widgetId) return;
        const next = (prefs?.hidden_widgets || []).filter((x) => x !== widgetId);
        savePreferences({ hidden_widgets: next });
        toast.success("Widget restored");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefs]);
    // iter-121.132 — SA-only PERMANENT remove from prebuilt. Calls the new
    // DELETE /dashboards/{id}/widgets/{wid} endpoint which wraps
    // _tool_remove_widget so propagation + activity-log match an
    // AI-Builder-driven remove. Refreshes the parent dashboard so the
    // widget disappears for the current viewer as well.
    const removeWidgetFromPrebuilt = useCallback(async (widgetId) => {
        if (!widgetId) return;
        if (!isSA) return;
        if (!window.confirm("Permanently remove this widget from the prebuilt? This affects every user.")) return;
        try {
            await api.delete(`/dashboards/${dashboard.id}/widgets/${widgetId}`);
            toast.success("Widget removed from prebuilt");
            // Fire the same refresh event the FAB uses after AI mutations
            // so the parent re-fetches the dashboard.
            window.dispatchEvent(new CustomEvent("dashboard:refresh", { detail: { dashboardId: dashboard.id } }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Remove failed");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboard?.id, isSA]);
    const setKpiSize = (kpiId, size) => {
        const sizeMap = { sm: 2, md: 3, lg: 6 };
        const cols = sizeMap[size] || 3;
        if (rearranging) {
            setDraftWidgets((prev) => {
                if (!prev) return prev;
                return prev.map((w) => (w.id === kpiId ? { ...w, colspan: cols } : w));
            });
        }
    };
    const setWidgetSize = (widgetId, size) => {
        const sizeMap = { sm: 4, md: 6, lg: 12 };
        const cols = sizeMap[size] || 6;
        if (rearranging) {
            setDraftWidgets((prev) => {
                if (!prev) return prev;
                return prev.map((w) => (w.id === widgetId ? { ...w, colspan: cols } : w));
            });
        }
    };
    const handleResizeStart = (e, id, type, direction) => {
        e.stopPropagation();
        e.preventDefault();
        const cardEl = e.currentTarget.parentElement;
        if (!cardEl) return;
        const startX = e.clientX;
        const startY = e.clientY;
        const startWidth = cardEl.getBoundingClientRect().width;
        const startHeight = cardEl.getBoundingClientRect().height;
        const parentEl = cardEl.parentElement;
        const parentWidth = parentEl ? parentEl.getBoundingClientRect().width : 1200;
        const isKpi = type === "kpi";
        const colWidth = parentWidth / 12;
        const rowHeight = isKpi ? 92 : 180;

        const handleMouseMove = (moveEv) => {
            const dx = moveEv.clientX - startX;
            const dy = moveEv.clientY - startY;
            if (direction === "east" || direction === "west") {
                const newWidth = direction === "east" ? startWidth + dx : startWidth - dx;
                let newCols = Math.round(newWidth / colWidth);
                const minColspan = isKpi ? 2 : (type === "table" ? 6 : 4);
                newCols = Math.max(minColspan, Math.min(12, newCols));
                setDraftWidgets((prev) => {
                    if (!prev) return prev;
                    return prev.map((w) => (w.id === id ? { ...w, colspan: newCols } : w));
                });
            } else if (direction === "south" || direction === "north") {
                const newHeight = direction === "south" ? startHeight + dy : startHeight - dy;
                let newRows = Math.round(newHeight / rowHeight);
                const minRowspan = 1;
                newRows = Math.max(minRowspan, Math.min(6, newRows));
                setDraftWidgets((prev) => {
                    if (!prev) return prev;
                    return prev.map((w) => (w.id === id ? { ...w, rowspan: newRows } : w));
                });
            }
        };
        const handleMouseUp = () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
    };
    const moveKpi = (kpiId, dir) => {
        // dir = -1 left / +1 right. Builds a concrete order from the current
        // effective list, mutates it, persists. Works even when prefs.kpi_order
        // is empty — the first move locks in an explicit order.
        const current = effectiveKpis.map((k) => k.id);
        const idx = current.indexOf(kpiId);
        if (idx < 0) return;
        const tgt = idx + dir;
        if (tgt < 0 || tgt >= current.length) return;
        const next = current.slice();
        [next[idx], next[tgt]] = [next[tgt], next[idx]];
        savePreferences({ kpi_order: next });
    };

    React.useEffect(() => {
        if (rearranging) {
            setDraftWidgets(dashboard.widgets || []);
            setDraftKpiOrder(effectiveKpis.map((k) => k.id));

            const initialKpiSizes = { ...(prefs?.kpi_sizes || {}) };
            effectiveKpis.forEach((k) => {
                if (k.id && k.size && !initialKpiSizes[k.id]) {
                    initialKpiSizes[k.id] = k.size;
                }
            });
            setDraftKpiSizes(initialKpiSizes);

            const initialWidgetSizes = { ...(prefs?.widget_sizes || {}) };
            (dashboard.widgets || []).forEach((w) => {
                if (w.id && w.size && !initialWidgetSizes[w.id]) {
                    initialWidgetSizes[w.id] = w.size;
                }
            });
            setDraftWidgetSizes(initialWidgetSizes);

            setDraftLayout(null);
        } else {
            setDraftWidgets(null);
            setDraftKpiOrder(null);
            setDraftKpiSizes(null);
            setDraftWidgetSizes(null);
            setDraftLayout(null);
        }
    }, [rearranging]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        const onLayoutChanged = (e) => {
            if (rearranging) {
                setDraftLayout(e.detail.layout);
            }
        };
        window.addEventListener("dashboard:layout-changed", onLayoutChanged);
        return () => window.removeEventListener("dashboard:layout-changed", onLayoutChanged);
    }, [rearranging]);

    const handleSaveRearrange = async () => {
        try {
            if (dashboard.previewMode) {
                const updates = {
                    expected_version: dashboard.source_template_version || dashboard.version || 1,
                    widgets: draftWidgets || dashboard.widgets || [],
                };
                if (draftKpiOrder) {
                    updates.locked_kpis = draftKpiOrder;
                }
                const r = await api.patch(`/gallery/${dashboard.id}`, updates);
                if (onDashboardUpdated) {
                    onDashboardUpdated(r.data);
                }
                toast.success("Template arrangement saved");
            } else {
                if (draftKpiOrder) {
                    const prefPatch = { kpi_order: draftKpiOrder };
                    const r = await api.patch(`/dashboards/${dashboard.id}/preferences`, prefPatch);
                    setPrefs(r.data);
                }
                const patch = {};
                if (draftWidgets) patch.widgets = draftWidgets;
                if (draftLayout) patch.layout = draftLayout;

                let updatedDashboard = dashboard;
                if (Object.keys(patch).length > 0) {
                    const r = await api.patch(`/dashboards/${dashboard.id}`, patch);
                    updatedDashboard = r.data;
                }
                if (onDashboardUpdated) {
                    onDashboardUpdated(updatedDashboard);
                }
                toast.success("Dashboard arrangement saved");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save arrangement");
        } finally {
            window.dispatchEvent(new CustomEvent("dashboard:toggle-rearrange"));
        }
    };

    const handleUndoRearrange = () => {
        setDraftWidgets(null);
        setDraftKpiOrder(null);
        setDraftKpiSizes(null);
        setDraftWidgetSizes(null);
        setDraftLayout(null);
        window.dispatchEvent(new CustomEvent("dashboard:toggle-rearrange"));
        toast.info("Arrangement changes discarded");
    };
    // iter-121.112 · Drag-reorder for KPI tiles in Rearrange mode.
    // KPI order persists via per-user `prefs.kpi_order` (NOT
    // `dashboard.widgets`) so each tenant/user has their own
    // arrangement of the same prebuilt template.
    //
    // PLACEMENT: this callback MUST be declared AFTER both
    // `effectiveKpis` (line ~990) and `savePreferences` (line ~1023).
    // Earlier placement caused a TDZ ReferenceError (iter-206 found
    // by testing agent) → white-screened every prebuilt dashboard.
    const reorderKpiByDrag = (srcId, tgtId) => {
        if (!srcId || !tgtId || srcId === tgtId) return;
        const current = effectiveKpis.map((k) => k.id);
        const srcIdx = current.indexOf(srcId);
        const tgtIdx = current.indexOf(tgtId);
        if (srcIdx < 0 || tgtIdx < 0) return;
        const next = current.slice();
        const [moved] = next.splice(srcIdx, 1);
        next.splice(tgtIdx, 0, moved);
        setDraftKpiOrder(next);
        // savePreferences with kpi_order is called transactionally on save
    };

    const swap = async (idx, kpiId) => {
        try {
            const r = await api.patch(`/dashboards/${dashboard.id}/slot/${idx}`, { kpi_id: kpiId });
            setPrefs(r.data);
            setActiveSwapSlot(null);
            toast.success(`Swapped slot ${idx + 1}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Swap failed");
        }
    };

    // "Suggest more KPIs" — fetches LLM-suggested canonical industry KPIs
    // that complement the existing widgets. Suggestions render in the pool
    // drawer with a one-click "Add" button that promotes them into the
    // dashboard's persistent kpi_pool via PATCH.
    const handleSuggestKpis = useCallback(async () => {
        if (suggestLoading || isPreview) return;
        setSuggestLoading(true);
        try {
            const r = await api.post(`/dashboards/${dashboard.id}/suggest-kpis`, { count: 5 });
            const out = Array.isArray(r.data?.suggestions) ? r.data.suggestions : [];
            setSuggestedKpis(out);
            if (out.length === 0) {
                toast.info("No new KPIs to suggest right now.");
            } else {
                toast.success(`${out.length} suggestion${out.length === 1 ? "" : "s"} ready — click Add to keep one.`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not fetch suggestions");
        } finally {
            setSuggestLoading(false);
        }
    }, [dashboard?.id, suggestLoading, isPreview]);

    const handleAddSuggestion = useCallback(async (sug) => {
        if (!sug || addingSuggestionId) return;
        setAddingSuggestionId(sug.id);
        try {
            // Strip the local-only `_suggested` flag before persisting.
            const { _suggested, why, ...widget } = sug;
            const nextWidgets = [...(dashboard.widgets || []), widget];
            const nextPool = [...(dashboard.kpi_pool || []), widget.id];
            await api.patch(`/dashboards/${dashboard.id}`, {
                widgets: nextWidgets,
                kpi_pool: nextPool,
            });
            // Mutate the dashboard prop in-place so the pool drawer reflects
            // the new candidate without a full page reload. The parent's next
            // re-fetch will replace this with canonical state.
            dashboard.widgets = nextWidgets;
            dashboard.kpi_pool = nextPool;
            // Drop the suggestion from the local list so it doesn't appear twice.
            setSuggestedKpis((prev) => prev.filter((s) => s.id !== sug.id));
            setRefreshKey((k) => k + 1);
            toast.success(`Added "${widget.title}" to pool`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not add KPI");
        } finally {
            setAddingSuggestionId(null);
        }
    }, [dashboard, addingSuggestionId]);

    const tabFilters = Array.isArray(dashboard.filters) ? dashboard.filters.filter((f) => f?.as_tab) : [];
    // Non-tab filters surface as a slide-down panel (date_range, multi_select, single_select).
    const panelFilters = Array.isArray(dashboard.filters) ? dashboard.filters.filter((f) => f && !f.as_tab) : [];

    // iter-119aa (P0) — Convert the backend `filter_discovery` payload
    // into FilterControl-compatible filter specs. Template filters always
    // win on field collision (template author has stronger intent than
    // auto-discovery). Date_range + boolean only render when there's no
    // existing template filter on the same field. `range` kinds are
    // skipped — FilterControl doesn't render numeric sliders today, and
    // we'd rather omit than misrepresent.
    const dynamicPanelFilters = useMemo(() => {
        if (!Array.isArray(dynamicFields) || dynamicFields.length === 0) return [];
        const takenFields = new Set(
            (Array.isArray(dashboard.filters) ? dashboard.filters : [])
                .map((f) => (f.field || "").split(".").pop())
                .filter(Boolean)
        );
        const out = [];
        for (const df of dynamicFields) {
            const leaf = (df.field || "").split(".").pop();
            if (!leaf || takenFields.has(leaf)) continue;
            if (df.kind === "date_range") {
                out.push({ id: `dyn_${leaf}`, field: leaf, type: "date_range", label: df.label, _dynamic: true });
            } else if (df.kind === "multi_select") {
                out.push({
                    id: `dyn_${leaf}`, field: leaf, type: "multi_select",
                    label: df.label, options: df.options || [], _dynamic: true
                });
            } else if (df.kind === "boolean") {
                out.push({
                    id: `dyn_${leaf}`, field: leaf, type: "multi_select",
                    label: df.label, options: ["true", "false"], _dynamic: true
                });
            } else if (df.kind === "search") {
                out.push({
                    id: `dyn_${leaf}`, field: leaf, type: "text",
                    label: df.label, _dynamic: true
                });
            } else if (df.kind === "range") {
                out.push({
                    id: `dyn_${leaf}`, field: leaf, type: "range",
                    label: df.label, min: df.min, max: df.max, _dynamic: true
                });
            }
            // `range` (numeric) now supported via the numeric slider added in iter-119ab.
        }
        return out;
    }, [dynamicFields, dashboard.filters]);

    const visibleDynamic = showAllDynamic
        ? dynamicPanelFilters
        : dynamicPanelFilters.slice(0, DYNAMIC_VISIBLE_DEFAULT);
    const activeFilterCount = Object.values(filterValues).filter((v) => v && (Array.isArray(v) ? v.length : (typeof v === "object" ? (v.from || v.to) : true))).length;
    // Slot count derives from the effective (user-filtered, ordered) KPI
    // list. Minimum 4 to always paint a grid; otherwise whatever the user's
    // prefs resolved. No hardcoded cap — LLM-driven (iter-72).
    // slotCount sizes dynamically to the effective KPI list — no hardcoded
    // cap (iter-72). Minimum 4 to always paint a visible grid.
    const slotCount = Math.max(4, effectiveKpis.length);

    // A cloned "My Dashboard" vs the prebuilt template. Prebuilt strip detail
    // (label, version, provenance) is hidden on clones (#4); Customize is
    // shown only on clones (#1). Template preview keeps everything.
    const isClone = !dashboard.is_template;
    const glossaryKpis = useMemo(() => {
        const seen = new Set(); const out = [];
        [...lockedKpis, ...kpiPool].forEach((k) => {
            if (!k?.id || seen.has(k.id)) return;
            seen.add(k.id);
            out.push({ id: k.id, title: k.title || k.id, key: k.key || k.metric_key });
        });
        return out;
    }, [lockedKpis, kpiPool]);
    const customizeCharts = useMemo(() => (dashboard.widgets || [])
        .filter((w) => w.chart_type !== "kpi" && !glossaryKpis.some((k) => k.id === w.id))
        .map((w) => ({ id: w.id, title: w.title || w.id })), [dashboard.widgets, glossaryKpis]);

    return (
        <div className="locked-kpi-strip mb-6" data-testid="prebuilt-renderer">
                {/* iter-119ae — filter dock. A compact header bar that always
                    shows the active filter count + a chevron to expand /
                    collapse the full panel. Reduces dashboard "noise" until
                    the user actively wants to filter. */}
                {(panelFilters.length > 0 || dynamicPanelFilters.length > 0 || (dashboard.filters || []).some((f) => f.type === "date_range")) && (
                    <div className="sticky top-[44px] z-30 mb-3" data-testid="prebuilt-filter-dock">
                        <div className="border border-border/60 rounded-md bg-card/95 backdrop-blur px-3 py-2">
                            <div className="flex items-center gap-x-3 gap-y-1.5 flex-wrap">
                                <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground shrink-0">Filters</span>
                                {(dashboard.filters || []).some((f) => f.type === "date_range") && (
                                    <DatePresetSelect value={datePreset} onChange={handleSetDatePreset} />
                                )}
                                {panelFilters.map((f) => (
                                    <FilterControl key={f.id || f.field} filter={f} compact
                                        dashboardId={dashboard.id}
                                        distinctUrl={distinctUrl}
                                        scope={scopeFilter}
                                        value={filterValues[f.id || f.field]}
                                        onChange={(v) => { if (f.type === "date_range") setDatePreset("custom"); setFilterValues((prev) => ({ ...prev, [f.id || f.field]: v })); }} />
                                ))}
                                {visibleDynamic.map((f) => (
                                    <div key={f.id} className="flex items-center gap-1" data-testid={`prebuilt-dyn-filter-${f.field}`}>
                                        <FilterControl filter={f} compact
                                            dashboardId={dashboard.id}
                                            distinctUrl={distinctUrl}
                                            scope={scopeFilter}
                                            value={filterValues[f.id]}
                                            onChange={(v) => setFilterValues((prev) => ({ ...prev, [f.id]: v }))} />
                                        <span className="text-[8px] font-mono uppercase tracking-wider opacity-50" title="Auto-derived from the dataset's columns">AUTO</span>
                                    </div>
                                ))}
                                {dynamicPanelFilters.length > DYNAMIC_VISIBLE_DEFAULT && (
                                    <button
                                        onClick={() => setShowAllDynamic((v) => !v)}
                                        className="text-[11px] font-mono uppercase tracking-wider text-primary hover:underline shrink-0"
                                        data-testid="prebuilt-dyn-filter-toggle"
                                    >
                                        {showAllDynamic
                                            ? "Show fewer"
                                            : `+${dynamicPanelFilters.length - DYNAMIC_VISIBLE_DEFAULT} more`}
                                    </button>
                                )}
                                {activeFilterCount > 0 && (
                                    <button
                                        onClick={handleClearFilters}
                                        className="ml-auto text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:text-destructive transition-colors shrink-0"
                                        data-testid="prebuilt-filter-clear"
                                    >
                                        Clear all
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

            {/* Header band — uses card styling so it adapts to the app theme. */}
            <div className="border border-border rounded-sm bg-card p-3">
                <div className="flex items-center justify-between mb-2.5 gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        {!isClone && <span className="text-[10px] font-mono uppercase tracking-wider opacity-70">Prebuilt KPI Strip</span>}
                        {!isClone && <Badge variant="outline" className="text-[10px] font-mono">v{dashboard.source_template_version || 1}</Badge>}
                        {dashboard.join_plan?.plan?.length > 0 && (
                            <Badge variant="outline" className="text-[10px] font-mono"
                                title={`Joined view across ${dashboard.join_plan.plan.length + 1} models`}
                                data-testid="prebuilt-joined-badge">
                                JOINED · {dashboard.join_plan.plan.length + 1} MODELS
                            </Badge>
                        )}
                        {liveLoading && <Loader2 className="h-3 w-3 animate-spin text-primary" />}
                        <ConnectorHealthPill dashboardId={dashboard?.id} />
                        {/* iter-119ab — SA scope pill, scoped to the dashboard
                            view per user feedback. Hidden for non-SA roles
                            inside the component itself. */}
                        <ScopeFilterPill />
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0" data-pdf-exclude="true">
                        <DashboardGlossaryButton kpis={glossaryKpis} />
                        {isClone && (
                            <CustomizeWidgetsDialog
                                dashboardId={dashboard.id}
                                kpis={glossaryKpis}
                                charts={customizeCharts}
                                prefs={prefs}
                                onSaved={(p) => setPrefs(p)}
                            />
                        )}
                        {/* iter-119aa (G3) — time-grain toggle. Affects all
                            line charts in the strip via the displayData
                            rebucketer inside PrebuiltChartCard. Persists
                            across reloads in localStorage per dashboard.
                            iter-120 round-3 — broadened the visibility
                            check so the toggle still shows after a
                            Regenerate that swapped line widgets for
                            other chart types: any widget with a
                            date-y `group_by` qualifies. */}
                        {((dashboard.widgets || []).some((w) => {
                            if (w.chart_type === "line") return true;
                            const gb = Array.isArray(w.group_by) ? w.group_by[0] : w.group_by;
                            if (!gb) return false;
                            const leaf = String(gb).replace(/^m\./, "").toLowerCase();
                            return leaf === "date" || leaf.endsWith("_date") || leaf === "created_at" || leaf === "month";
                        })) && (
                                <div className="hidden md:flex items-center border border-border rounded-sm overflow-hidden"
                                    data-testid="prebuilt-time-grain-pill">
                                    {[
                                        { k: "day", l: "D" },
                                        { k: "week", l: "W" },
                                        { k: "month", l: "M" },
                                    ].map((g) => (
                                        <button key={g.k}
                                            onClick={() => {
                                                setTimeGrain(g.k);
                                                try { localStorage.setItem(`analyzio.dash.${dashboard?.id}.grain`, g.k); }
                                                catch { /* ignore */ }
                                                // iter-120 round-4 · visible confirmation so the
                                                // user sees SOMETHING when they click — the
                                                // line-chart rebucket can be invisible on data
                                                // with only 1-2 distinct dates (which collapses
                                                // to the same single bar after grouping).
                                                toast.success(`Switched to ${g.k === "day" ? "Daily" : g.k === "week" ? "Weekly" : "Monthly"} view`);
                                            }}
                                            className={`h-7 w-8 text-[11px] font-mono uppercase tracking-wider ${(timeGrain === g.k || (timeGrain === "auto" && g.k === "day")) ? "bg-primary text-primary-foreground" : "hover:bg-secondary/40"}`}
                                            title={`Switch chart aggregation to ${g.k}`}
                                            data-testid={`prebuilt-time-grain-${g.k}`}>
                                            {g.l}
                                        </button>
                                    ))}
                                </div>
                            )}
                        {rearranging && (
                            <>
                                <Button size="sm" variant="default"
                                    className="h-7 rounded-sm text-[11px]"
                                    onClick={handleSaveRearrange}
                                    title="Save arrangement changes"
                                    data-testid="prebuilt-save-btn">
                                    Save
                                </Button>
                                <Button size="sm" variant="outline"
                                    className="h-7 rounded-sm text-[11px]"
                                    onClick={handleUndoRearrange}
                                    title="Discard arrangement changes"
                                    data-testid="prebuilt-undo-btn">
                                    Undo
                                </Button>
                            </>
                        )}
                        {poolOpen && (
                            <Button size="sm" variant="default" className="h-7 rounded-sm text-[11px]" onClick={() => setPoolOpen(false)}
                                title="Close the KPI pool" data-testid="prebuilt-pool-close-chip">
                                <Layers className="h-3 w-3 mr-1" />Pool · {kpiPool.length}<X className="h-3 w-3 ml-1" />
                            </Button>
                        )}
                        {/* Secondary actions — same handlers & gates as before, grouped in ⋯ */}
                        <Popover open={historyOpen} onOpenChange={(o) => { setHistoryOpen(o); if (o && !versions) loadVersions(); }}>
                            <PopoverAnchor asChild>
                                <span className="inline-flex">
                                    <DropdownMenu modal={false}>
                                        <DropdownMenuTrigger asChild>
                                            <Button size="sm" variant="outline" className="h-7 w-7 p-0 rounded-sm" aria-label="More dashboard actions" title="More actions" data-testid="prebuilt-more-actions">
                                                <MoreHorizontal className="h-4 w-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-64" data-testid="prebuilt-more-menu">
                                            {!isPreview && (
                                                <DropdownMenuItem onSelect={handleRegenerate} data-testid="prebuilt-regenerate-btn"
                                                    title="Re-runs every query with fresh data and adds charts for newly available data columns. Existing widgets are kept.">
                                                    <RefreshCw className="h-3.5 w-3.5 mr-2 mt-0.5 self-start" />
                                                    <span><span className="block">Regenerate widgets</span><span className="block text-[10px] text-muted-foreground">Adds charts for newly available data columns</span></span>
                                                </DropdownMenuItem>
                                            )}
                                            {(!dashboard.previewMode || isEditableTemplate) && !rearranging && (
                                                <DropdownMenuItem onSelect={() => window.dispatchEvent(new CustomEvent("dashboard:toggle-rearrange"))}
                                                    title="Toggle drag-to-rearrange / resize on custom widgets below" data-testid="prebuilt-rearrange-btn">
                                                    <Move className="h-3.5 w-3.5 mr-2" />Rearrange
                                                </DropdownMenuItem>
                                            )}
                                            {templateId && (
                                                <DropdownMenuItem onSelect={() => { setHistoryOpen(true); if (!versions) loadVersions(); }}
                                                    title="View saved versions of this prebuilt dashboard and restore a previous one" data-testid="prebuilt-history-toggle">
                                                    <History className="h-3.5 w-3.5 mr-2" />History
                                                </DropdownMenuItem>
                                            )}
                                            <DropdownMenuItem onSelect={() => setPoolOpen((v) => !v)} data-testid="prebuilt-pool-drawer-toggle">
                                                <Layers className="h-3.5 w-3.5 mr-2" />KPI Pool · {kpiPool.length}{poolOpen && <Check className="h-3.5 w-3.5 ml-auto" />}
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </span>
                            </PopoverAnchor>
                                <PopoverContent align="end" className="w-80 p-0" data-testid="prebuilt-history-panel">
                                    <div className="px-3 py-2 border-b border-border">
                                        <div className="text-xs font-semibold">Version history</div>
                                        <div className="text-[10px] text-muted-foreground font-mono">Prebuilt · saved by Sense AI Builder</div>
                                    </div>
                                    <div className="max-h-72 overflow-y-auto p-2 text-[11px]">
                                        {versions === null && <div className="px-1 py-2 text-muted-foreground">Loading…</div>}
                                        {versions !== null && versions.length === 0 && (
                                            <div className="px-1 py-2 text-muted-foreground">No prior versions yet — this is the first save.</div>
                                        )}
                                        {versions && versions.length > 0 && versions.map((v) => (
                                            <div key={v.snapshot_id} className="flex items-center gap-2 px-1 py-1.5 border-b border-border last:border-0">
                                                <span className="font-mono">v{v.version}</span>
                                                <span className="text-muted-foreground">{fmtHistoryDate(v.snapshotted_at)}</span>
                                                {v.snapshotted_by && <span className="text-muted-foreground truncate max-w-[90px]" title={v.snapshotted_by}>{v.snapshotted_by}</span>}
                                                {Number(v.version) === currentTemplateVersion ? (
                                                    <span className="ml-auto text-[10px] text-primary font-semibold uppercase tracking-wider border border-primary/20 px-2 py-0.5 rounded-sm bg-primary/5">
                                                        current
                                                    </span>
                                                ) : canRestoreVersion ? (
                                                    <button
                                                        onClick={() => restoreVersion(v.snapshot_id)}
                                                        disabled={restoringVersion === v.snapshot_id}
                                                        className="ml-auto px-2 py-0.5 rounded-sm border border-border hover:bg-primary/10 hover:border-primary/40 disabled:opacity-50 uppercase tracking-wider"
                                                        data-testid={`prebuilt-history-restore-${v.snapshot_id}`}>
                                                        {restoringVersion === v.snapshot_id ? "…" : "Restore"}
                                                    </button>
                                                ) : (
                                                    <span
                                                        className="ml-auto text-[10px] text-muted-foreground italic"
                                                        title="Read-only — only org/entity admins or super-admins can restore versions."
                                                        data-testid={`prebuilt-history-restore-readonly-${v.snapshot_id}`}>
                                                        view-only
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </PopoverContent>
                        </Popover>
                    </div>
                </div>


                {tabFilters.length > 0 && (
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-3">
                        <TabsList data-testid="prebuilt-tab-filters">
                            <TabsTrigger value="all" data-testid="prebuilt-tab-all">All</TabsTrigger>
                            {tabFilters.map((f) => (
                                <TabsTrigger key={f.id || f.name} value={f.id || f.name}
                                    data-testid={`prebuilt-tab-${f.id || f.name}`}>
                                    {f.label || f.name}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                        <TabsContent value={activeTab} />
                    </Tabs>
                )}

                {/* Empty-state CTA — shows when joined-query resolved nothing.
                    Three flavours, in priority order:
                      1. iter-120 · SA pinned a scope with zero rows → offer
                         "Clear scope" so the SA can pop back to all-tenants
                         instead of staring at a silent empty dock.
                      2. Active filters narrowed the data to zero → "Clear
                         filters".
                      3. No data at all → "Connect a source".
                    No hardcoded scope-aware copy: the displayed scope label
                    comes from `scopeFilter.label` (set by the TopBar pill)
                    so any tenant/org name flows through verbatim. */}
                {empty && !liveLoading && (
                    <div className="border border-dashed border-amber-500/40 rounded-sm p-6 mb-3 bg-amber-500/5 flex items-start gap-3"
                        data-testid="prebuilt-empty-state">
                        <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium">
                                {scopeFilter?.isSA && (scopeFilter.tenantId || scopeFilter.orgId)
                                    ? `No data for the pinned scope${scopeFilter.label ? ` (${scopeFilter.label})` : ""}`
                                    : activeFilterCount > 0
                                        ? "No data matches your active filters"
                                        : "No data resolved for this dashboard yet"}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                                {scopeFilter?.isSA && (scopeFilter.tenantId || scopeFilter.orgId)
                                    ? "The tenant/org you pinned via the scope pill has no rows in any of the canonical models bound to this dashboard. Clear the scope to see data across all tenants."
                                    : activeFilterCount > 0
                                        ? `Try widening your selection — clear the date range or status filter to see all available data.`
                                        : `The canonical model${dashboard.data_model_bindings?.length === 1 ? "" : "s"} this template binds to (${(dashboard.data_model_bindings || []).map((b) => typeof b === "string" ? b : b.model_id).join(", ") || "—"}) returned 0 rows for your tenant. Connect a data source to enable this dashboard.`
                                }
                            </div>
                            <div className="flex gap-2 mt-3">
                                {scopeFilter?.isSA && (scopeFilter.tenantId || scopeFilter.orgId) ? (
                                    <button onClick={() => scopeFilter.clear?.()}
                                        className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-primary hover:underline"
                                        data-testid="prebuilt-empty-clear-scope">
                                        Clear pinned scope →
                                    </button>
                                ) : activeFilterCount > 0 ? (
                                    <button onClick={handleClearFilters}
                                        className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-primary hover:underline"
                                        data-testid="prebuilt-empty-clear-filters">
                                        Clear all filters →
                                    </button>
                                ) : (
                                    // iter-119x (#3): banner now fires the SAME flow as the
                                    // tile "Connect Data" button (AddSourceDialog → wizard),
                                    // instead of navigating away to /data?tab=connectors and
                                    // losing dashboard context. Parent DashboardDetailView
                                    // listens for `dashboard:open-add-source` and opens the
                                    // dialog.
                                    <button
                                        type="button"
                                        onClick={() => {
                                            window.dispatchEvent(new CustomEvent("dashboard:open-add-source", {
                                                detail: { id: dashboard.id, source: "empty-banner" },
                                            }));
                                        }}
                                        className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-primary hover:underline"
                                        data-testid="prebuilt-empty-connect">
                                        <Database className="h-3 w-3" /> Connect data →
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-2" data-testid="prebuilt-kpi-strip">
                            {Array.from({ length: slotCount }).map((_, i) => {
                                const k = slotKpi(i);
                                // iter-121.112 · Wrap each KPI in a drag handler
                                // when Rearrange mode is on. The wrapper takes
                                // the drag events; KpiTile's internal click
                                // (drill-down) still fires on plain click. KPI
                                // order persists via `prefs.kpi_order` (NOT
                                // `dashboard.widgets`) so each user has their
                                // own arrangement.
                                const kpiDragProps = (rearranging && k) ? {
                                    draggable: true,
                                    onDragStart: (e) => {
                                        if (e.target.closest('.cursor-ew-resize') || e.target.closest('.cursor-ns-resize')) {
                                            e.preventDefault();
                                            return;
                                        }
                                        setDragSourceId(k.id);
                                        e.dataTransfer.effectAllowed = "move";
                                        // Tag so charts/KPI drop-targets can
                                        // refuse cross-type drops.
                                        e.dataTransfer.setData("text/x-analyzio-kind", "kpi");
                                    },
                                    onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; },
                                    onDrop: (e) => {
                                        e.preventDefault();
                                        // Only accept KPI→KPI drops here.
                                        if (e.dataTransfer.getData("text/x-analyzio-kind") !== "kpi") return;
                                        reorderKpiByDrag(dragSourceId, k.id);
                                        setDragSourceId(null);
                                    },
                                    onDragEnd: () => setDragSourceId(null),
                                } : {};
                                const wrapperRingClass = (rearranging && k)
                                    ? "relative ring-2 ring-primary/40 ring-offset-2 ring-offset-background rounded-md cursor-move transition-shadow hover:ring-primary"
                                    : "";
                                const colspan = k ? (k.colspan || 3) : 3;
                                const rowspan = k ? (k.rowspan || 1) : 1;
                                return (
                                    <div key={k ? (k.id || i) : i}
                                        className={wrapperRingClass}
                                        style={{
                                            gridColumn: isMobile ? "span 12" : `span ${colspan}`,
                                            gridRow: isMobile ? "span 1" : `span ${rowspan}`
                                        }}
                                        data-testid={`prebuilt-kpi-wrap-${i}`}
                                        {...kpiDragProps}>
                                        <KpiTile
                                            kpi={k ? { ...k, delta: liveDeltas[k.id] ?? k.delta } : k}
                                            slotIndex={i}
                                            isFirst={i === 0}
                                            isLast={i >= effectiveKpis.length - 1}
                                            liveValue={k ? liveValues[k.id] : null}
                                            livePriorValue={k ? livePriorValues[k.id] : null}
                                            size={k ? (kpiSizes[k.id] || "md") : "md"}
                                            rearranging={rearranging}
                                            isSwapOpen={activeSwapSlot === i}
                                            pool={kpiPool}
                                            slotOverride={prefs?.slot_overrides?.[String(i)]}
                                            onMove={moveKpi}
                                            onResize={setKpiSize}
                                            onSwapToggle={(idx) => setActiveSwapSlot(activeSwapSlot === idx ? null : idx)}
                                            onSwap={swap}
                                            onSwapReset={(idx) => swap(idx, null)}
                                            onHide={isPreview ? undefined : removeFromStrip}
                                            onRemoveFromPrebuilt={isPreview ? (isEditableTemplate ? removeWidgetFromPrebuilt : undefined) : (isSA ? removeWidgetFromPrebuilt : undefined)}
                                            readOnly={isPreview ? !isEditableTemplate : false}
                                            onDrillDown={() => k && setDrillDownKpi(k)}
                                        />
                                        {rearranging && k && !isMobile && (
                                            <>
                                                {/* East edge handle */}
                                                <div className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-primary/20 z-30 select-none"
                                                    onMouseDown={(e) => handleResizeStart(e, k.id, "kpi", "east")}
                                                    onDragStart={(e) => e.preventDefault()} />
                                                {/* West edge handle */}
                                                <div className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-primary/20 z-30 select-none"
                                                    onMouseDown={(e) => handleResizeStart(e, k.id, "kpi", "west")}
                                                    onDragStart={(e) => e.preventDefault()} />
                                                {/* South edge handle */}
                                                <div className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize hover:bg-primary/20 z-30 select-none"
                                                    onMouseDown={(e) => handleResizeStart(e, k.id, "kpi", "south")}
                                                    onDragStart={(e) => e.preventDefault()} />
                                                {/* North edge handle */}
                                                <div className="absolute top-0 left-0 right-0 h-2 cursor-ns-resize hover:bg-primary/20 z-30 select-none"
                                                    onMouseDown={(e) => handleResizeStart(e, k.id, "kpi", "north")}
                                                    onDragStart={(e) => e.preventDefault()} />
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {poolOpen && (
                        <aside className="border border-border rounded-sm bg-secondary/30 p-3 w-[260px] shrink-0"
                            data-testid="prebuilt-pool-drawer">
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">KPI Pool</div>
                                <button onClick={() => setPoolOpen(false)} className="text-muted-foreground hover:text-foreground">
                                    <X className="h-3 w-3" />
                                </button>
                            </div>
                            {/* Suggest more KPIs — only available on live (non-preview)
                                dashboards. Fetches LLM-curated canonical industry KPIs
                                that complement what's already on screen. */}
                            {!isPreview && (
                                <Button
                                    size="sm" variant="outline"
                                    className="w-full h-7 rounded-sm text-[11px] mb-2 justify-start"
                                    onClick={handleSuggestKpis}
                                    disabled={suggestLoading}
                                    data-help="Ask Sense AI to suggest 3-6 canonical industry KPIs that complement this dashboard."
                                    data-testid="prebuilt-suggest-kpis-btn">
                                    {suggestLoading ? (
                                        <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                                    ) : (
                                        <Sparkles className="h-3 w-3 mr-1.5" />
                                    )}
                                    {suggestLoading ? "Suggesting…" : "Suggest more KPIs"}
                                </Button>
                            )}
                            {suggestedKpis.length > 0 && (
                                <div className="mb-2 space-y-1.5"
                                    data-testid="prebuilt-suggestions-list">
                                    <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 flex items-center gap-1">
                                        <Sparkles className="h-2.5 w-2.5" />
                                        AI Suggestions · {suggestedKpis.length}
                                    </div>
                                    {suggestedKpis.map((s) => (
                                        <div key={s.id}
                                            className="border border-primary/40 rounded-sm p-2 text-[11px] bg-primary/5"
                                            data-testid={`prebuilt-suggestion-${s.id}`}>
                                            <div className="font-mono mb-0.5">{s.title}</div>
                                            <div className="text-muted-foreground truncate text-[10px]" title={s.formula}>{s.formula}</div>
                                            {s.why && (
                                                <div className="text-muted-foreground italic text-[10px] mt-0.5 line-clamp-2">{s.why}</div>
                                            )}
                                            <div className="flex gap-1 mt-1.5">
                                                <Button
                                                    size="sm" variant="default"
                                                    className="h-6 rounded-sm text-[10px] px-2"
                                                    onClick={() => handleAddSuggestion(s)}
                                                    disabled={addingSuggestionId === s.id}
                                                    data-testid={`prebuilt-suggestion-add-${s.id}`}>
                                                    {addingSuggestionId === s.id ? (
                                                        <Loader2 className="h-2.5 w-2.5 mr-1 animate-spin" />
                                                    ) : (
                                                        <Check className="h-2.5 w-2.5 mr-1" />
                                                    )}
                                                    Add
                                                </Button>
                                                <Button
                                                    size="sm" variant="ghost"
                                                    className="h-6 rounded-sm text-[10px] px-2"
                                                    onClick={() => setSuggestedKpis((prev) => prev.filter((x) => x.id !== s.id))}
                                                    data-testid={`prebuilt-suggestion-dismiss-${s.id}`}>
                                                    Dismiss
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="space-y-1.5 max-h-[320px] overflow-y-auto">
                                {kpiPool.length === 0 && suggestedKpis.length === 0 && hiddenIds.size === 0 && (
                                    <div className="text-[11px] text-muted-foreground py-4 text-center">
                                        No swap candidates.
                                    </div>
                                )}
                                {kpiPool.map((c) => (
                                    <div key={c.id}
                                        className="border border-border rounded-sm p-2 text-[11px] cursor-grab"
                                        draggable
                                        onDragStart={(e) => e.dataTransfer.setData("kpiId", c.id)}
                                        data-testid={`prebuilt-pool-candidate-${c.id}`}>
                                        <div className="font-mono">{c.title}</div>
                                        {c.formula && <div className="text-muted-foreground truncate" title={c.formula}>{c.formula}</div>}
                                    </div>
                                ))}
                                {/* Hidden-by-user KPIs — restore affordance */}
                                {hiddenIds.size > 0 && (
                                    <>
                                        <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 pt-2"
                                            data-testid="prebuilt-hidden-kpis-heading">
                                            Hidden · {hiddenIds.size}
                                        </div>
                                        {lockedKpis.filter((k) => hiddenIds.has(k.id)).map((k) => (
                                            <div key={k.id}
                                                className="border border-dashed border-border rounded-sm p-2 text-[11px] bg-secondary/10"
                                                data-testid={`prebuilt-hidden-kpi-${k.id}`}>
                                                <div className="flex items-start justify-between gap-1">
                                                    <div className="font-mono truncate flex-1">{k.title}</div>
                                                    <button onClick={() => restoreToStrip(k.id)}
                                                        className="text-primary hover:underline text-[10px] shrink-0"
                                                        data-testid={`prebuilt-hidden-kpi-restore-${k.id}`}>
                                                        Restore
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}
                                {/* iter-121.132 — Hidden-by-user CHART / TABLE / MAP widgets
                                    listed alongside KPIs so the user can restore them from
                                    the same drawer they hid them from. */}
                                {Array.isArray(prefs?.hidden_widgets) && prefs.hidden_widgets.length > 0 && (() => {
                                    const hiddenWidgetSet = new Set(prefs.hidden_widgets);
                                    const hiddenList = (dashboard.widgets || []).filter(
                                        (w) => hiddenWidgetSet.has(w.id) && w.chart_type !== "kpi",
                                    );
                                    if (hiddenList.length === 0) return null;
                                    return (
                                        <>
                                            <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 pt-2"
                                                data-testid="prebuilt-hidden-widgets-heading">
                                                Hidden charts/tables · {hiddenList.length}
                                            </div>
                                            {hiddenList.map((w) => (
                                                <div key={w.id}
                                                    className="border border-dashed border-border rounded-sm p-2 text-[11px] bg-secondary/10"
                                                    data-testid={`prebuilt-hidden-widget-${w.id}`}>
                                                    <div className="flex items-start justify-between gap-1">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="font-mono truncate">{w.title}</div>
                                                            <div className="text-muted-foreground text-[10px]">{w.chart_type}</div>
                                                        </div>
                                                        <button onClick={() => restoreWidgetForUser(w.id)}
                                                            className="text-primary hover:underline text-[10px] shrink-0"
                                                            data-testid={`prebuilt-hidden-widget-restore-${w.id}`}>
                                                            Restore
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </>
                                    );
                                })()}
                            </div>
                        </aside>
                    )}
                </div>
            </div>

            {/* iter-121.112 · Rearrange banner — MOVED out of the chart
                IIFE so it always shows when the user toggles Rearrange,
                even on dashboards where no chart has live data yet. The
                earlier placement (inside the chart IIFE) hid the banner
                on KPI-only dashboards → clicking Rearrange looked like
                a no-op. */}
            {rearranging && (
                <div className="border border-primary/40 rounded-sm bg-primary/5 px-3 py-2 mt-3 text-[11px]"
                    data-testid="prebuilt-rearrange-banner">
                    <span><Move className="inline h-3 w-3 mr-1" />
                        Rearrange mode · Drag any KPI tile, chart card, or table to reorder. Drop on another widget to swap positions.
                    </span>
                </div>
            )}

            {/* Chart/Table widgets resolved via /joined-query — only those with data
                show. Empty-state widgets are hidden so the dashboard never
                renders blank Recharts skeletons. */}
            {(() => {
                const lockedIds = new Set(lockedKpis.map((k) => k.id));
                const userHiddenWidgets = new Set(prefs?.hidden_widgets || []);
                const gridWidgets = currentWidgets.filter((w) => {
                    if (lockedIds.has(w.id) || w.chart_type === "kpi") return false;
                    if (userHiddenWidgets.has(w.id)) return false;
                    if (w.chart_type === "table") return true;
                    if (w.chart_type === "line" || w.chart_type === "bar" || w.chart_type === "horizontal_bar" || w.chart_type === "donut" || w.chart_type === "pie" || w.chart_type === "geo_map" || w.chart_type === "treemap" || w.chart_type === "area" || w.chart_type === "combo" || w.chart_type === "composed") {
                        return true;
                    }
                    return false;
                });
                // charts.length === 0 && tables.length === 0
                if (gridWidgets.length === 0) return null;
                return (
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mt-3" data-testid="prebuilt-charts">
                        {gridWidgets.map((w) => {
                            const colspan = w.colspan || (w.chart_type === "table" ? 12 : 6);
                            const rowspan = w.rowspan || 2;
                            const dragProps = rearranging ? {
                                draggable: true,
                                onDragStart: (e) => {
                                    if (e.target.closest('.cursor-ew-resize') || e.target.closest('.cursor-ns-resize')) {
                                        e.preventDefault();
                                        return;
                                    }
                                    setDragSourceId(w.id);
                                    e.dataTransfer.effectAllowed = "move";
                                    e.dataTransfer.setData("text/x-analyzio-kind", "chart");
                                },
                                onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; },
                                onDrop: (e) => {
                                    e.preventDefault();
                                    if (e.dataTransfer.getData("text/x-analyzio-kind") !== "chart") return;
                                    reorderWidget(dragSourceId, w.id);
                                    setDragSourceId(null);
                                },
                                onDragEnd: () => setDragSourceId(null),
                            } : {};
                            return (
                                <div key={w.id}
                                    className={`${rearranging ? "relative ring-2 ring-primary/40 ring-offset-2 ring-offset-background cursor-move rounded-sm transition-shadow hover:ring-primary" : ""}`}
                                    style={{
                                        gridColumn: isMobile ? "span 12" : `span ${colspan}`,
                                        gridRow: isMobile ? "span 1" : `span ${rowspan}`
                                    }}
                                    data-testid={`prebuilt-chart-wrap-${w.id}`}
                                    {...dragProps}>
                                    <div className={rearranging ? "pointer-events-none select-none h-full" : "h-full"}>
                                        {w.chart_type === "table" ? (
                                            <TablePrebuiltCard
                                                widget={w} liveData={liveTables[w.id]} dashboardId={dashboard?.id}
                                                onHide={isPreview ? undefined : hideWidgetForUser}
                                                onRemoveFromPrebuilt={isPreview ? (isEditableTemplate ? removeWidgetFromPrebuilt : undefined) : (isSA ? removeWidgetFromPrebuilt : undefined)}
                                                onResize={setWidgetSize}
                                                currentSize={currentWidgetSizes[w.id]}
                                                onDashboardUpdated={onDashboardUpdated}
                                                onLoadMore={loadMoreTableRows}
                                                isLoadingMore={!!tableLoadingMore[w.id]}
                                            />
                                        ) : (
                                            <PrebuiltChartCard
                                                widget={w} liveData={liveCharts[w.id]}
                                                timeGrain={timeGrain} dashboardId={dashboard?.id}
                                                onHide={isPreview ? undefined : hideWidgetForUser}
                                                onRemoveFromPrebuilt={isPreview ? (isEditableTemplate ? removeWidgetFromPrebuilt : undefined) : (isSA ? removeWidgetFromPrebuilt : undefined)}
                                                onResize={setWidgetSize}
                                                currentSize={currentWidgetSizes[w.id]}
                                                onDashboardUpdated={onDashboardUpdated}
                                            />
                                        )}
                                    </div>
                                    {rearranging && !isMobile && (
                                        <>
                                            {/* East edge handle */}
                                            <div className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-primary/20 z-30 select-none"
                                                onMouseDown={(e) => handleResizeStart(e, w.id, "widget", "east")}
                                                onDragStart={(e) => e.preventDefault()} />
                                            {/* West edge handle */}
                                            <div className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-primary/20 z-30 select-none"
                                                onMouseDown={(e) => handleResizeStart(e, w.id, "widget", "west")}
                                                onDragStart={(e) => e.preventDefault()} />
                                            {/* South edge handle */}
                                            <div className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize hover:bg-primary/20 z-30 select-none"
                                                onMouseDown={(e) => handleResizeStart(e, w.id, "widget", "south")}
                                                onDragStart={(e) => e.preventDefault()} />
                                            {/* North edge handle */}
                                            <div className="absolute top-0 left-0 right-0 h-2 cursor-ns-resize hover:bg-primary/20 z-30 select-none"
                                                onMouseDown={(e) => handleResizeStart(e, w.id, "widget", "north")}
                                                onDragStart={(e) => e.preventDefault()} />
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })()}
            <KpiDrillDownDialog
                open={!!drillDownKpi}
                onOpenChange={(v) => { if (!v) setDrillDownKpi(null); }}
                kpi={drillDownKpi}
                dashboard={dashboard}
                filterPayload={(() => {
                    if (!drillDownKpi) return _filterPayload;
                    const wFilters = drillDownKpi.filters || drillDownKpi.where || [];
                    return [...(_filterPayload || []), ...(wFilters)];
                })()}
                decorate={_decorate}
                queryUrl={queryUrl}
            />
            {/* iter-120 · Chart drill-down dialog. Same component as
                the KPI drill-down; we synthesize a KPI-shaped object
                from the clicked widget and AND its `group_by` filter
                onto the user's filterPayload so the rows are pinned
                to the slice they clicked. NO hardcoded column rules. */}
            <KpiDrillDownDialog
                open={!!drillDownChart}
                onOpenChange={(v) => { if (!v) setDrillDownChart(null); }}
                kpi={drillDownChart ? (() => {
                    const w = drillDownChart.widget;
                    // ISSUE-FIX: Resolve matching series formula for combo / multi-series / stacked charts
                    let seriesFormula = null;
                    if (drillDownChart.seriesName && Array.isArray(w.series)) {
                        const match = w.series.find(s => s?.name === drillDownChart.seriesName || s?.title === drillDownChart.seriesName || s?.dataKey === drillDownChart.seriesName);
                        if (match?.formula) seriesFormula = match.formula;
                    }
                    const formula = seriesFormula
                        || w.formula
                        || (w.value_column ? `SUM(m.${w.value_column})` : null);
                    return {
                        id: `chart-drill-${w.id}`,
                        title: `${w.title || "Chart"} · ${drillDownChart.label}${drillDownChart.seriesName ? ` (${drillDownChart.seriesName})` : ""}`,
                        formula: formula,
                        group_by: w.group_by,
                        dimensions: w.dimensions,
                    };
                })() : null}
                dashboard={dashboard}
                filterPayload={(() => {
                    if (!drillDownChart) return _filterPayload;
                    const gb = Array.isArray(drillDownChart.widget.group_by)
                        ? drillDownChart.widget.group_by[0]
                        : drillDownChart.widget.group_by;
                    // iter-120 round-3 · Strip the canonical `m.` or model-level `c.` prefix
                    // that widgets carry on `group_by` (e.g. `m.device`, `c.campaign_name`).
                    // The drill-down endpoint expects the bare field name (it prepends `data.` itself).
                    // Use the SAME key shape the other filters use:
                    // `{model, field, op, value}` — NOT operator/values.
                    const fieldName = gb ? (String(gb).includes(".") ? String(gb).split(".").pop() : gb) : null;
                    // Pull the bound model's slug so the extra filter
                    // routes through joined_query correctly when other
                    // call sites consume `filterPayload`.
                    const bindings = dashboard?.data_model_bindings || [];
                    const firstBinding = bindings[0];
                    const rawSlug = typeof firstBinding === "string"
                        ? firstBinding
                        : (firstBinding?.model_id || "");
                    const slug = rawSlug.includes(".") ? rawSlug.split(".").pop() : rawSlug;
                    const extra = [];
                    if (fieldName) {
                        let applied = false;
                        if (drillDownChart.widget && ["bar", "line", "area", "combo", "composed"].includes(drillDownChart.widget.chart_type)) {
                            if (timeGrain === "month" && /^\d{4}-\d{2}$/.test(drillDownChart.label)) {
                                const [yy, mm] = drillDownChart.label.split("-");
                                const startDate = `${yy}-${mm}-01`;
                                const d = new Date(Date.UTC(parseInt(yy, 10), parseInt(mm, 10), 1));
                                const endDate = d.toISOString().slice(0, 10);
                                extra.push({ model: slug, field: fieldName, op: "gte", value: startDate });
                                extra.push({ model: slug, field: fieldName, op: "lt", value: endDate });
                                applied = true;
                            } else if (timeGrain === "week" && /^\d{4}-\d{2}-\d{2}$/.test(drillDownChart.label)) {
                                const startDate = drillDownChart.label;
                                const d = new Date(startDate);
                                d.setUTCDate(d.getUTCDate() + 7);
                                const endDate = d.toISOString().slice(0, 10);
                                extra.push({ model: slug, field: fieldName, op: "gte", value: startDate });
                                extra.push({ model: slug, field: fieldName, op: "lt", value: endDate });
                                applied = true;
                            }
                        }
                        if (!applied) {
                            extra.push({ model: slug, field: fieldName, op: "eq", value: drillDownChart.label });
                        }
                    }
                    if (drillDownChart.seriesName) {
                        const dim = Array.isArray(drillDownChart.widget.dimensions)
                            ? drillDownChart.widget.dimensions[0]
                            : null;
                        const dimField = dim ? (dim.field || dim) : null;
                        let dimFieldName = dimField ? (String(dimField).includes(".") ? String(dimField).split(".").pop() : dimField) : null;
                        // ISSUE-FIX: If dimensions not explicitly declared, check secondary group_by for stacked/grouped charts
                        if (!dimFieldName && Array.isArray(drillDownChart.widget.group_by) && drillDownChart.widget.group_by.length > 1) {
                            const gb2 = drillDownChart.widget.group_by[1];
                            dimFieldName = gb2 ? (String(gb2).includes(".") ? String(gb2).split(".").pop() : gb2) : null;
                        }
                        if (dimFieldName) {
                            extra.push({ model: slug, field: dimFieldName, op: "eq", value: drillDownChart.seriesName });
                        }
                    }
                    const wFilters = drillDownChart.widget.filters || drillDownChart.widget.where || [];
                    return [...(_filterPayload || []), ...(wFilters), ...extra];
                })()}
                decorate={_decorate}
                queryUrl={queryUrl}
            />
        </div>
    );
}

const DATE_PRESETS = [["7d", "Last 7 days"], ["30d", "Last 30 days"], ["90d", "Last 90 days"], ["ytd", "Year to date"], ["all", "All time"]];

// Same keys and handler as the former 7d/30d/90d/YTD/All buttons.
function DatePresetSelect({ value, onChange }) {
    return (
        <Select value={value} onValueChange={onChange}>
            <SelectTrigger className="h-7 w-[140px] text-[11px] rounded-sm" aria-label="Date range" data-testid="prebuilt-period-presets">
                <CalendarRange className="h-3.5 w-3.5 mr-1 opacity-60" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
                {DATE_PRESETS.map(([k, l]) => <SelectItem key={k} value={k} className="text-xs" data-testid={`prebuilt-period-${k}`}>{l}</SelectItem>)}
                {value === "custom" && <SelectItem value="custom" disabled className="text-xs" data-testid="prebuilt-period-custom">Custom range</SelectItem>}
            </SelectContent>
        </Select>
    );
}
